import * as signalR from '@microsoft/signalr';
import { useNotifications } from '../../hooks/useNotifications';
import { PhotoUpload } from '../../components/PhotoUpload';
import { AddressAutocomplete, parseAddressComponents } from '../../components/AddressAutocomplete';
import { DateTimePicker } from '../../components/DateTimePicker';
import { PaymentForm, SubscriptionPaymentForm, MilestonePaymentForm } from '../../components/PaymentForm';
import { CancelJobModal } from '../../components/CancelJobModal';
import { PriceAdjustmentModal } from '../../components/PriceAdjustmentModal';
import { EstimateApprovalModal } from '../../components/EstimateApprovalModal';
import { DonationCenterDetailsModal } from '../../components/DonationCenterDetailsModal';
import { CustomerEstimatesView } from './CustomerEstimatesView';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, Job, QuoteResponse, CustomerProfile, SubscriptionPlan, CustomerSubscriptionDetail, AdItem, EstimateDto, JobLineItemDto, InvoiceDto, PaymentScheduleDto, WorkOrderDto, DonationCenterDto, CustomerBinStatusDto, ShreddingBinSizeDto, GiftVoucherPurchaseResponse } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useFeature, useFeatureFlagsReady } from '../../context/FeatureFlagContext';
import { useTracking } from '../../hooks/useTracking';
import { Star } from 'lucide-react';
import { PageShell, Card, Btn, Input, Select, Badge, Alert, Spinner, StatCard, StarRating, TrackerCard, C } from '../../components/UI';
import { useBranding } from '../../context/BrandingContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Fallback for addresses saved without lat/lng (e.g. a legacy default address) — geocodes
// via the Google Maps JS SDK already loaded for AddressAutocomplete, so the service-area
// check and job creation always have real coordinates to work with.
function geocodeAddress(address: string): Promise<{ lat: number; lng: number; city?: string; state?: string; county?: string; postalCode?: string; country?: string } | null> {
    return new Promise(resolve => {
        if (!window.google?.maps) { resolve(null); return; }
        new window.google.maps.Geocoder().geocode({ address, componentRestrictions: { country: 'us' } }, (results: any, status: string) => {
            if (status !== 'OK' || !results?.[0]) { resolve(null); return; }
            const result = results[0];
            resolve({
                lat: result.geometry.location.lat(),
                lng: result.geometry.location.lng(),
                ...parseAddressComponents(result.address_components),
            });
        });
    });
}

interface MessageItem { id: string; jobId: string; senderId: string; senderRole: string; text: string; sentAt: string; }

// Fixed size + pinned CTA so every top-level Services card renders the same regardless of how
// long its title/description text is — matches the ad/plan carousel below it. Any new card
// added to the Services screen should go through this component rather than a hand-copied
// block, so it can't drift back into the old "different height per card" look.
export function ServiceCard({ icon, title, description, buttonText, onClick }: {
    icon: string; title: string; description: string; buttonText: string; onClick: () => void;
}) {
    return (
        <div onClick={onClick} style={{ cursor: 'pointer', flexShrink: 0 }}>
            <Card style={{ textAlign: 'center', width: 260, height: 280, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '28px 20px 20px', boxSizing: 'border-box' }}>
                <div style={{ fontSize: 44, flexShrink: 0 }}>{icon}</div>
                <div style={{ fontSize: 17, fontWeight: 700, marginTop: 10, flexShrink: 0 }}>{title}</div>
                <div style={{ fontSize: 13, color: C.textSub, marginTop: 6, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>{description}</div>
                <div style={{ marginTop: 'auto', width: '100%', flexShrink: 0 }}><Btn fullWidth onClick={onClick}>{buttonText}</Btn></div>
            </Card>
        </div>
    );
}

// By the time a PaymentForm's onSuccess fires, Stripe has already authorized the card. This call
// only asks our backend to re-read the PaymentIntent and flip the job/order to Authorized so a
// contractor can see it. It used to be wrapped in a bare `catch {}` — so any transient failure
// (network blip, a stale token, a brief races) silently stranded the job in "Payment required"
// with the customer's card already on hold and nothing to reconcile it (the Stripe webhook is the
// only backstop, and only if it's configured for the live key). Retry a few times, and if it
// still fails, tell the customer rather than showing "Booking confirmed".
async function confirmAuthWithRetry(fn: () => Promise<void>, label: string): Promise<void> {
    for (let attempt = 1; attempt <= 4; attempt++) {
        try { await fn(); return; }
        catch (e) {
            if (attempt === 4) {
                console.error(`confirmAuth failed after retries (${label})`, e);
                alert(
                    "Your card was authorized, but we hit a snag finalizing the booking. It should " +
                    "reconcile automatically within a minute. If the pickup still shows \"Payment " +
                    "required\", tap Complete Payment again or contact support — you have not been charged twice."
                );
                return;
            }
            await new Promise(r => setTimeout(r, 1500 * attempt));
        }
    }
}

type View = 'activity' | 'services' | 'track' | 'book' | 'dumpster' | 'estimate' | 'profile' | 'plans' | 'purchase-history' | 'gift';

export default function CustomerPortal() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const branding = useBranding();
    const svcNoun = branding.serviceNoun ?? null;    // e.g. "pickup" or null → generic
    const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
    const svcLabel = svcNoun ?? 'service';           // singular: "pickup" | "service"
    const svcLabelPlural = svcNoun ? svcNoun + 's' : 'services'; // plural
    const dumpsterEnabled = useFeature('dumpsterRental');
    const loadingJobsEnabled = useFeature('loadingJobs');
    const estimatesEnabled = useFeature('estimatesAndQuotes');
    const estimateBuilderEnabled = useFeature('estimateBuilder');
    // Feature flags start at hardcoded client defaults and flip to the real tenant config a beat
    // later. For flags that turn a whole Services card on/off, guessing "on" by default while
    // dumpsterRental is actually off for this tenant meant the Dumpster card would mount, then
    // unmount once the real config arrived — a visible flash + reflow of the cards next to it.
    // Wait for the real value before deciding whether to render these cards at all.
    const flagsReady = useFeatureFlagsReady();
    const [searchParams] = useSearchParams();
    const deepLinkEstimateId = searchParams.get('estimateId');
    const [view, setView] = useState<View>(deepLinkEstimateId ? 'estimate' : 'services');
    const [jobs, setJobs] = useState<Job[]>([]);
    const [loading, setLoading] = useState(true);
    const [trackingJobId, setTrackingJobId] = useState<string | null>(null);
    const [activeJob, setActiveJob] = useState<Job | null>(null);
    const [cancellingJob, setCancellingJob] = useState<Job | null>(null);
    const [estimateAlerts, setEstimateAlerts] = useState<{ job: Job; estimate: EstimateDto }[]>([]);
    const [alertPanelOpen, setAlertPanelOpen] = useState(false);

    // TREK-78 follow-up: surface pending estimates via a bell in the nav (mirrors the admin
    // dashboard's alert bell) — previously the only way to see one was to open live tracking.
    useEffect(() => {
        if (!estimatesEnabled) { setEstimateAlerts([]); return; }
        let cancelled = false;
        const check = async () => {
            const inProgress = jobs.filter(j => j.status === 'InProgress');
            if (inProgress.length === 0) { if (!cancelled) setEstimateAlerts([]); return; }
            const results = await Promise.all(inProgress.map(async j => {
                const est = await api.getEstimate(j.id).catch(() => null);
                return est?.status === 'Pending' ? { job: j, estimate: est } : null;
            }));
            if (!cancelled) setEstimateAlerts(results.filter((r): r is { job: Job; estimate: EstimateDto } => r != null));
        };
        check();
        const interval = setInterval(check, 15000);
        return () => { cancelled = true; clearInterval(interval); };
    }, [estimatesEnabled, jobs]);

    const { location, connected, error: trackingError } = useTracking(trackingJobId);

    useNotifications(user?.userId ?? null, user?.role ?? null);

    useEffect(() => { if (!user || user.role !== 'Customer') navigate('/'); }, [user, navigate]);
    useEffect(() => { fetchJobs(); }, []);

    const fetchJobs = useCallback(async () => {
        try { setJobs(await api.getMyJobs()); } catch { } finally { setLoading(false); }
    }, []);

    // The list only ever loaded once on mount — accept/status/price-adjustment events only
    // reached whichever single job the customer had open on the live Tracking screen (via
    // useTracking's own hub connection), so anyone sitting on the main jobs list needed a
    // manual page refresh to see a job move to Accepted, InProgress, etc. Subscribe to every
    // non-terminal job's group here too and patch the matching row in place.
    const activeJobIds = jobs.filter(j => j.status !== 'Completed' && j.status !== 'Cancelled').map(j => j.id).join(',');
    useEffect(() => {
        if (!activeJobIds) return;
        const ids = activeJobIds.split(',');
        const token = localStorage.getItem('br_token');
        const hub = new signalR.HubConnectionBuilder()
            .withUrl(`${API_BASE}/hubs/tracking?access_token=${token}`, { withCredentials: false })
            .withAutomaticReconnect()
            .build();

        const applyStatus = (jobId: string, status?: string) => {
            if (!status) return;
            setJobs(prev => prev.map(j => j.id === jobId ? { ...j, status } : j));
        };

        hub.on('ContractorLocationUpdated', (push: { jobId: string; jobStatus?: string }) => applyStatus(push.jobId, push.jobStatus));
        hub.on('JobStatusUpdated', (push: { jobId: string; jobStatus?: string }) => applyStatus(push.jobId, push.jobStatus));
        hub.on('PriceAdjustmentUpdated', (push: { jobId: string }) => applyStatus(push.jobId, 'PriceAdjustmentPending'));

        const connect = () =>
            hub.start()
                .then(() => Promise.all(ids.map(id => hub.invoke('SubscribeToJob', id))))
                .catch(console.error);
        connect();

        // This hub is mounted for the whole CustomerPortal shell (book/estimate/activity all sit
        // under it), so it's open while the customer is mid-booking taking an AI-estimate photo.
        // An open WebSocket makes the tab ineligible for Chrome's back/forward cache — on Android,
        // backgrounding this tab to launch the camera (via <input capture>) then gets treated as a
        // full kill-and-reload instead of a restore, wiping the in-progress form and the photo that
        // was just taken. Stopping the hub while hidden removes that blocker; reconnect on return.
        const onVisibility = () => {
            if (document.hidden) hub.stop().catch(() => {});
            else if (hub.state === signalR.HubConnectionState.Disconnected) connect();
        };
        document.addEventListener('visibilitychange', onVisibility);

        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            hub.stop();
        };
    }, [activeJobIds]);

    const trackJob = async (job: Job) => {
        setActiveJob(job);
        setTrackingJobId(job.id);
        setView('track');
    };

    const handleCancelJob = async (jobId: string, reason: string) => {
        try {
            await api.cancelJob(jobId, reason);
            setCancellingJob(null);
            fetchJobs();
        } catch (e: any) {
            alert(e.message);
        }
    };

    const nav = (
        <>
            {(['services', 'activity'] as View[]).map(v => (
                <button key={v} onClick={() => setView(v as View)} style={{ background: 'transparent', border: 'none', borderBottom: `2px solid ${view === v ? 'var(--color-accent, #4ADE80)' : 'transparent'}`, color: view === v ? 'var(--color-accent, #4ADE80)' : 'rgba(255,255,255,0.7)', padding: '6px 14px', borderRadius: 0, cursor: 'pointer', fontWeight: view === v ? 700 : 400, fontSize: 13 }}>
                    {v === 'services' ? '+ Services' : 'Activity'}
                </button>
            ))}
            {estimatesEnabled && (
                <div style={{ position: 'relative' }}>
                    <button
                        onClick={() => setAlertPanelOpen(o => !o)}
                        title="Alerts"
                        style={{ position: 'relative', background: 'transparent', border: 'none', color: estimateAlerts.length > 0 ? '#FBBF24' : 'rgba(255,255,255,0.7)', padding: '6px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>
                        🔔
                        {estimateAlerts.length > 0 && (
                            <span style={{ position: 'absolute', top: 2, right: 2, background: '#DC2626', color: '#fff', borderRadius: '50%', minWidth: 15, height: 15, fontSize: 9, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px' }}>
                                {estimateAlerts.length}
                            </span>
                        )}
                    </button>
                    {alertPanelOpen && (
                        <>
                            <div onClick={() => setAlertPanelOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 200 }} />
                            <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 6, width: 300, background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', zIndex: 201, overflow: 'hidden' }}>
                                <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}`, fontWeight: 700, fontSize: 13, color: C.text }}>
                                    Estimates awaiting approval
                                </div>
                                {estimateAlerts.length === 0 ? (
                                    <div style={{ padding: 20, textAlign: 'center', color: C.textMuted, fontSize: 12 }}>Nothing needs your review</div>
                                ) : estimateAlerts.map(({ job, estimate }) => (
                                    <div key={job.id} style={{ padding: '10px 14px', borderBottom: `1px solid ${C.borderLight}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                                        <div style={{ minWidth: 0 }}>
                                            <div style={{ fontWeight: 600, fontSize: 12, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Estimate #E-{estimate.number}</div>
                                            <div style={{ fontSize: 11, color: C.textSub }}>{job.serviceType} · ${estimate.totalAmount.toFixed(2)}</div>
                                        </div>
                                        <button
                                            onClick={() => { setAlertPanelOpen(false); setView('activity'); }}
                                            style={{ background: 'transparent', border: 'none', fontSize: 11, color: C.green, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', padding: 0 }}>
                                            Review →
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}
            <button onClick={() => setView('profile')} title="Account settings" style={{ background: 'transparent', border: 'none', color: view === 'profile' || view === 'plans' ? 'var(--color-accent, #4ADE80)' : 'rgba(255,255,255,0.7)', padding: '6px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>⚙</button>
            <button onClick={() => { logout(); navigate('/'); }} style={{ background: 'transparent', border: '1px solid var(--color-accent, #4ADE80)', color: 'var(--color-accent, #4ADE80)', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Sign out</button>
        </>
    );

    return (
        <PageShell title={`Hi, ${user?.name?.split(' ')[0]} 👋`} nav={nav}>
            {cancellingJob && (
                <CancelJobModal
                    jobId={cancellingJob.id}
                    onConfirm={(reason) => handleCancelJob(cancellingJob.id, reason)}
                    onClose={() => setCancellingJob(null)}
                />
            )}
            {view === 'activity' && (
                <>
                    <JobsList
                        jobs={jobs}
                        loading={loading}
                        onTrack={trackJob}
                        onRefresh={fetchJobs}
                        onCancel={(job) => setCancellingJob(job)}
                    />
                    {flagsReady && dumpsterEnabled && <DumpsterOrdersSection />}
                </>
            )}
            {view === 'services' && (
                <div>
                    <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
                        <div style={{ display: 'flex', gap: 20, minWidth: 'max-content' }}>
                            <ServiceCard
                                icon={svcNoun === 'pickup' ? '📦' : '🛠️'}
                                title={svcNoun ? `Schedule a ${cap(svcNoun)}` : 'Request a Service'}
                                description={branding.serviceDescription ?? 'We come to you — book online in minutes'}
                                buttonText="Book now →"
                                onClick={() => setView('book')}
                            />
                            {flagsReady && dumpsterEnabled && (
                                <ServiceCard icon="🚛" title="Rent a Dumpster" description="Delivery + optional loading runners"
                                    buttonText="Browse dumpsters →" onClick={() => setView('dumpster')} />
                            )}
                            {flagsReady && estimateBuilderEnabled && (
                                <ServiceCard icon="📋" title="Request/View an Estimate" description="Request a quote or check on one you've sent"
                                    buttonText="Request Now →" onClick={() => setView('estimate')} />
                            )}
                            <ServiceCard icon="🎁" title="Send a Gift" description="Buy a pickup for someone else — we'll email them the code"
                                buttonText="Send a gift →" onClick={() => setView('gift')} />
                        </div>
                    </div>
                    <div style={{ marginTop: 28 }}>
                        <AdCarousel onViewPlans={() => setView('plans')} />
                    </div>
                </div>
            )}
            {view === 'book' && (
                <>
                    <button onClick={() => setView('services')} style={{ background: 'none', border: 'none', color: C.green, fontWeight: 600, fontSize: 14, cursor: 'pointer', marginBottom: 16, padding: 0 }}>← Back to Services</button>
                    <BookingFlow onComplete={() => { fetchJobs(); setView('activity'); }} onDumpster={() => setView('dumpster')} />
                </>
            )}
            {view === 'gift' && (
                <>
                    <button onClick={() => setView('services')} style={{ background: 'none', border: 'none', color: C.green, fontWeight: 600, fontSize: 14, cursor: 'pointer', marginBottom: 16, padding: 0 }}>← Back to Services</button>
                    <SendGiftFlow onComplete={() => setView('services')} />
                </>
            )}
            {view === 'dumpster' && (
                <>
                    <button onClick={() => setView('services')} style={{ background: 'none', border: 'none', color: C.green, fontWeight: 600, fontSize: 14, cursor: 'pointer', marginBottom: 16, padding: 0 }}>← Back to Services</button>
                    <DumpsterView onComplete={() => setView('activity')} />
                </>
            )}
            {view === 'estimate' && (
                <>
                    <button onClick={() => setView('services')} style={{ background: 'none', border: 'none', color: C.green, fontWeight: 600, fontSize: 14, cursor: 'pointer', marginBottom: 16, padding: 0 }}>← Back to Services</button>
                    <CustomerEstimatesView userName={user?.name} userEmail={user?.email} initialSelectedId={deepLinkEstimateId} />
                </>
            )}
            {view === 'track' && activeJob && (
                <TrackingView
                    job={activeJob}
                    location={location}
                    connected={connected}
                    connectionError={trackingError}
                    onBack={() => setView('activity')}
                    onRefresh={fetchJobs}
                />
            )}
            {view === 'profile' && (
                <CustomerProfilePanel onViewPlans={() => setView('plans')} onViewHistory={() => setView('purchase-history')} />
            )}
            {view === 'plans' && (
                <>
                    <button onClick={() => setView('profile')} style={{ background: 'none', border: 'none', color: C.green, fontWeight: 600, fontSize: 14, cursor: 'pointer', marginBottom: 16, padding: 0 }}>← Back to Profile</button>
                    <SubscriptionPlansPanel onBack={() => setView('profile')} />
                </>
            )}
            {view === 'purchase-history' && (
                <>
                    <button onClick={() => setView('profile')} style={{ background: 'none', border: 'none', color: C.green, fontWeight: 600, fontSize: 14, cursor: 'pointer', marginBottom: 16, padding: 0 }}>← Back to Profile</button>
                    <PurchaseHistoryPanel />
                </>
            )}
        </PageShell>
    );
}

// ── Customer Profile Panel ─────────────────────────────────────────────────────

function CustomerProfilePanel({ onViewPlans, onViewHistory }: { onViewPlans: () => void; onViewHistory: () => void }) {
    const branding = useBranding();
    const contractorLabelLower = (branding.contractorNoun || 'Contractor').toLowerCase();
    const [profile, setProfile] = useState<CustomerProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [defaultAddress, setDefaultAddress] = useState('');
    const [defaultLat, setDefaultLat] = useState<number | undefined>();
    const [defaultLng, setDefaultLng] = useState<number | undefined>();
    const [jobUpdates, setJobUpdates] = useState(true);
    const [promotions, setPromotions] = useState(false);
    const [saved, setSaved] = useState(false);
    const [portalLoading, setPortalLoading] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [showPasswordForm, setShowPasswordForm] = useState(false);
    const [currentPw, setCurrentPw] = useState('');
    const [newPw, setNewPw] = useState('');
    const [confirmPw, setConfirmPw] = useState('');
    const [pwSaving, setPwSaving] = useState(false);
    const [pwError, setPwError] = useState('');
    const [pwSaved, setPwSaved] = useState(false);
    const [showEmailForm, setShowEmailForm] = useState(false);
    const [emailPw, setEmailPw] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [emailSaved, setEmailSaved] = useState(false);
    const [phoneError, setPhoneError] = useState('');
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        api.getCustomerProfile().then(p => {
            setProfile(p);
            setName(p.name);
            setPhone(p.phoneNumber ?? '');
            setDefaultAddress(p.defaultAddress ?? '');
            setDefaultLat(p.defaultLat ?? undefined);
            setDefaultLng(p.defaultLng ?? undefined);
            setJobUpdates(p.notificationPrefs.jobUpdates);
            setPromotions(p.notificationPrefs.promotions);
        }).catch(() => {}).finally(() => setLoading(false));
    }, []);

    const cancelSub = async () => {
        if (!window.confirm('Cancel at the end of this billing period? You\'ll keep access until then.')) return;
        setCancelling(true);
        try {
            await api.cancelSubscription(false);
            const updated = await api.getCustomerProfile();
            setProfile(updated);
        } catch (e: any) { alert(e.message); } finally { setCancelling(false); }
    };

    const save = async () => {
        setPhoneError('');
        if (phone) {
            const digits = phone.replace(/\D/g, '');
            if (digits.length < 10) { setPhoneError('Phone must be at least 10 digits.'); return; }
        }
        setSaving(true);
        try {
            const updated = await api.updateCustomerProfile({
                name, phoneNumber: phone || undefined,
                notificationPrefs: { jobUpdates, promotions },
                defaultAddress: defaultAddress || undefined,
                defaultLat: defaultLat,
                defaultLng: defaultLng,
            });
            setProfile(updated);
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch { } finally { setSaving(false); }
    };

    const changeEmail = async () => {
        setEmailError('');
        if (!newEmail.includes('@')) { setEmailError('Enter a valid email address.'); return; }
        try {
            await api.changeEmail(emailPw, newEmail);
            setProfile(prev => prev ? { ...prev, email: newEmail } : prev);
            setEmailPw(''); setNewEmail('');
            setEmailSaved(true); setShowEmailForm(false);
            setTimeout(() => setEmailSaved(false), 3000);
        } catch (e: any) { setEmailError(e.message || 'Email change failed.'); }
    };

    const changePassword = async () => {
        setPwError('');
        if (newPw !== confirmPw) { setPwError('New passwords do not match.'); return; }
        if (newPw.length < 8) { setPwError('Password must be at least 8 characters.'); return; }
        setPwSaving(true);
        try {
            await api.changePassword(currentPw, newPw);
            setCurrentPw(''); setNewPw(''); setConfirmPw('');
            setPwSaved(true); setShowPasswordForm(false);
            setTimeout(() => setPwSaved(false), 3000);
        } catch (e: any) { setPwError(e.message || 'Password change failed.'); }
        finally { setPwSaving(false); }
    };

    const deleteAccount = async () => {
        if (!window.confirm('Permanently delete your account? This cannot be undone. Your personal data will be removed and any active subscription cancelled.')) return;
        // Was only a second generic "type OK" confirm — anyone with momentary access to an
        // unlocked, already-logged-in session could delete the account with no credential
        // required, unlike password/email change on this same screen. Now requires the real
        // password, verified server-side.
        const password = window.prompt('Enter your password to confirm account deletion:');
        if (!password) return;
        setDeleting(true);
        try {
            await api.deleteAccount(password);
            window.location.href = '/';
        } catch (e: any) { alert(e.message); setDeleting(false); }
    };

    const openPortal = async () => {
        setPortalLoading(true);
        try {
            const { url } = await api.getStripePortalUrl();
            window.open(url, '_blank');
        } catch { } finally { setPortalLoading(false); }
    };

    if (loading) return <Spinner />;

    const sub = profile?.activeSubscription;
    return (
        <div style={{ maxWidth: 560 }}>
            <h2 style={{ margin: '0 0 20px', fontSize: 20, fontWeight: 700 }}>Account</h2>

            <Card style={{ marginBottom: 16 }}>
                <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 600 }}>Contact info</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <Input label="Name" value={name} onChange={setName} />
                    <Input label="Phone" value={phone} onChange={v => { setPhone(v); setPhoneError(''); }} />
                    {phoneError && <div style={{ color: '#DC3545', fontSize: 13, marginTop: -8 }}>{phoneError}</div>}
                    <AddressAutocomplete
                        label="Default service address"
                        value={defaultAddress}
                        placeholder="Optional — pre-fills on new bookings"
                        onChange={r => { setDefaultAddress(r.address); setDefaultLat(r.lat); setDefaultLng(r.lng); }}
                    />
                    <div style={{ color: C.textSub, fontSize: 13 }}>{profile?.email}</div>
                </div>
            </Card>

            <Card style={{ marginBottom: 16 }}>
                <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 600 }}>Notifications</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
                        <div>
                            <div style={{ fontWeight: 500, fontSize: 14 }}>Job updates</div>
                            <div style={{ fontSize: 12, color: C.textSub }}>Status changes, {contractorLabelLower} arrival, completion</div>
                        </div>
                        <input type="checkbox" checked={jobUpdates} onChange={e => setJobUpdates(e.target.checked)} style={{ width: 18, height: 18, cursor: 'pointer', accentColor: C.green }} />
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
                        <div>
                            <div style={{ fontWeight: 500, fontSize: 14 }}>Promotions</div>
                            <div style={{ fontSize: 12, color: C.textSub }}>Discounts and seasonal offers</div>
                        </div>
                        <input type="checkbox" checked={promotions} onChange={e => setPromotions(e.target.checked)} style={{ width: 18, height: 18, cursor: 'pointer', accentColor: C.green }} />
                    </label>
                </div>
            </Card>

            <Card style={{ marginBottom: 16 }}>
                <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 600 }}>Subscription</h3>
                {sub ? (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                            <div>
                                <span style={{ fontWeight: 600 }}>{sub.planName}</span>
                                <span style={{ color: C.textSub, marginLeft: 8, fontSize: 13 }}>${sub.monthlyPrice}{billingPeriodSuffix(sub.billingInterval)}</span>
                            </div>
                            <Badge label={sub.status} />
                        </div>
                        {sub.includedPickups && (
                            <div style={{ fontSize: 13, color: C.textSub, marginBottom: 8 }}>
                                {sub.pickupsUsed} / {sub.includedPickups} {sub.serviceLabel} used this period
                            </div>
                        )}
                        {sub.willCancelAtPeriodEnd && (
                            <Alert type="error" message="Cancels at end of billing period" />
                        )}
                        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                            <Btn variant="outline" onClick={openPortal} disabled={portalLoading}>
                                {portalLoading ? 'Opening…' : 'Manage payment'}
                            </Btn>
                            <Btn variant="outline" onClick={onViewPlans}>Change plan</Btn>
                            {!sub.willCancelAtPeriodEnd && (
                                <Btn variant="danger" onClick={cancelSub} disabled={cancelling}>
                                    {cancelling ? 'Cancelling…' : 'Cancel plan'}
                                </Btn>
                            )}
                        </div>
                    </div>
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ color: C.textSub, fontSize: 14 }}>No active plan</span>
                        <Btn onClick={onViewPlans}>View plans</Btn>
                    </div>
                )}
            </Card>

            <Card style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={onViewHistory}>
                    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Purchase history</h3>
                    <span style={{ color: C.green, fontWeight: 600, fontSize: 14 }}>View past orders →</span>
                </div>
            </Card>

            <Card style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Password</h3>
                    <Btn variant="outline" onClick={() => { setShowPasswordForm(v => !v); setPwError(''); }}>
                        {showPasswordForm ? 'Cancel' : 'Change password'}
                    </Btn>
                </div>
                {showPasswordForm && (
                    <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <Input label="Current password" value={currentPw} onChange={setCurrentPw} type="password" />
                        <Input label="New password" value={newPw} onChange={setNewPw} type="password" />
                        <Input label="Confirm new password" value={confirmPw} onChange={setConfirmPw} type="password" />
                        {pwError && <div style={{ color: '#DC3545', fontSize: 13 }}>{pwError}</div>}
                        <Btn onClick={changePassword} disabled={pwSaving}>{pwSaving ? 'Updating…' : 'Update password'}</Btn>
                    </div>
                )}
                {pwSaved && <div style={{ color: C.green, fontSize: 13, marginTop: 8, fontWeight: 600 }}>Password updated ✓</div>}
            </Card>

            <Card style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Email address</h3>
                    <Btn variant="outline" onClick={() => { setShowEmailForm(v => !v); setEmailError(''); }}>
                        {showEmailForm ? 'Cancel' : 'Change email'}
                    </Btn>
                </div>
                {showEmailForm && (
                    <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <Input label="Current password" value={emailPw} onChange={setEmailPw} type="password" />
                        <Input label="New email address" value={newEmail} onChange={setNewEmail} type="email" />
                        {emailError && <div style={{ color: '#DC3545', fontSize: 13 }}>{emailError}</div>}
                        <Btn onClick={changeEmail}>{emailSaved ? 'Email updated ✓' : 'Update email'}</Btn>
                    </div>
                )}
                {emailSaved && <div style={{ color: C.green, fontSize: 13, marginTop: 8, fontWeight: 600 }}>Email updated ✓</div>}
            </Card>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 24 }}>
                <Btn onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</Btn>
                {saved && <span style={{ color: C.green, fontSize: 13, fontWeight: 600 }}>Saved ✓</span>}
            </div>

            {branding.supportEmail && (
            <Card>
                <h3 style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 600 }}>Contact us</h3>
                <div style={{ fontSize: 13, color: C.textSub, lineHeight: '1.7' }}>
                    <div>Need help? Reach our support team:</div>
                    <div><a href={`mailto:${branding.supportEmail}`} style={{ color: 'var(--color-button, #1D9E75)' }}>{branding.supportEmail}</a></div>
                </div>
            </Card>
            )}

            <Card style={{ borderColor: '#DC3545' + '44' }}>
                <h3 style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 600, color: '#DC3545' }}>Danger zone</h3>
                <div style={{ fontSize: 13, color: C.textSub, marginBottom: 12 }}>
                    Permanently delete your account and all associated data. Any active subscription will be cancelled immediately. This cannot be undone.
                </div>
                <Btn variant="danger" onClick={deleteAccount} disabled={deleting}>
                    {deleting ? 'Deleting…' : 'Delete account'}
                </Btn>
            </Card>
        </div>
    );
}

// ── Purchase History Panel ──────────────────────────────────────────────────────
// Reached only via "View past orders" on the Profile panel — total spent (Completed jobs
// only, same exclusion as everywhere else) plus each order's invoice download.

function PurchaseHistoryPanel() {
    const [jobs, setJobs] = useState<Job[]>([]);
    const [loading, setLoading] = useState(true);
    const [invoices, setInvoices] = useState<Record<string, InvoiceDto | null>>({});

    useEffect(() => {
        api.getMyJobs().then(all => {
            const completed = all.filter(j => j.status === 'Completed').sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt));
            setJobs(completed);
            completed.forEach(j => {
                api.getInvoice(j.id).then(inv => setInvoices(prev => ({ ...prev, [j.id]: inv })));
            });
        }).finally(() => setLoading(false));
    }, []);

    const totalSpent = jobs.reduce((s, j) => s + (j.grandTotal ?? j.totalPrice), 0);

    if (loading) return <Spinner />;

    return (
        <div>
            <div style={{ display: 'flex', gap: 16, marginBottom: 28, flexWrap: 'wrap' }}>
                <StatCard label="Orders" value={jobs.length} />
                <StatCard label="Total spent" value={`$${totalSpent.toFixed(2)}`} color={C.greenMid} />
            </div>
            {jobs.length === 0 ? (
                <div style={{ color: C.textSub, textAlign: 'center', padding: 40 }}>No completed orders yet.</div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {jobs.map(job => {
                        const invoice = invoices[job.id];
                        return (
                            <Card key={job.id}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div>
                                        <div style={{ fontWeight: 600 }}>{job.serviceType}</div>
                                        <div style={{ fontSize: 13, color: C.textSub, marginTop: 2 }}>{job.pickupAddress}</div>
                                        <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>{new Date(job.createdAt).toLocaleDateString()}</div>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
                                        <div style={{ fontWeight: 700, fontSize: 16 }}>${(job.grandTotal ?? job.totalPrice).toFixed(2)}</div>
                                        {invoice && (
                                            <Btn size="sm" variant="outline" onClick={() => api.downloadInvoicePdf(job.id, invoice.number)}>📄 Invoice</Btn>
                                        )}
                                    </div>
                                </div>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ── Subscription Plans Panel ───────────────────────────────────────────────────

const billingPeriodSuffix = (interval?: string) => {
    switch (interval) {
        case 'Quarterly':  return '/quarter';
        case 'SemiAnnual': return '/6 mo';
        case 'Annual':     return '/year';
        default:           return '/mo';
    }
};

function SubscriptionPlansPanel({ onBack }: { onBack: () => void }) {
    const branding = useBranding();
    const svcLabel = branding.serviceNoun ?? 'service';
    const svcLabelPlural = branding.serviceNoun ? branding.serviceNoun + 's' : 'services';
    const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
    const [activeSub, setActiveSub] = useState<CustomerSubscriptionDetail | null | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [subscribing, setSubscribing] = useState<string | null>(null);
    const [switching, setSwitching] = useState<string | null>(null);
    const [cancelling, setCancelling] = useState(false);
    const [pendingPayment, setPendingPayment] = useState<{ clientSecret: string; planName: string; amount: number } | null>(null);
    const [subSuccess, setSubSuccess] = useState(false);

    useEffect(() => {
        Promise.all([api.getSubscriptionPlans(), api.getActiveSubscription()])
            .then(([p, s]) => { setPlans(p); setActiveSub(s); })
            .catch(() => setActiveSub(null))
            .finally(() => setLoading(false));
    }, []);

    const subscribe = async (plan: SubscriptionPlan) => {
        setSubscribing(plan.id);
        try {
            const result = await api.subscribe(plan.id);
            if (!result.clientSecret) throw new Error('Payment setup failed — plan may not be fully configured.');
            setPendingPayment({ clientSecret: result.clientSecret, planName: plan.name, amount: plan.monthlyPrice });
        } catch (e: any) { alert(e.message); } finally { setSubscribing(null); }
    };

    const onPaymentSuccess = async () => {
        setPendingPayment(null);
        setSubSuccess(true);
        const updated = await api.getActiveSubscription().catch(() => null);
        setActiveSub(updated);
    };

    const cancel = async () => {
        if (!window.confirm('Cancel at the end of this billing period?')) return;
        setCancelling(true);
        try {
            await api.cancelSubscription(false);
            const updated = await api.getActiveSubscription();
            setActiveSub(updated);
        } catch (e: any) { alert(e.message); } finally { setCancelling(false); }
    };

    const changePlan = async (plan: SubscriptionPlan) => {
        const direction = plan.monthlyPrice > (activeSub?.monthlyPrice ?? 0) ? 'Upgrade' : 'Downgrade';
        if (!window.confirm(`${direction} to ${plan.name} ($${plan.monthlyPrice}${billingPeriodSuffix(plan.billingInterval)})? Proration will be applied for the remaining billing period.`)) return;
        setSwitching(plan.id);
        try {
            await api.changePlan(plan.id);
            const updated = await api.getActiveSubscription();
            setActiveSub(updated);
        } catch (e: any) { alert(e.message); } finally { setSwitching(null); }
    };

    if (loading) return <Spinner />;

    if (pendingPayment) {
        return (
            <div style={{ maxWidth: 520 }}>
                <button onClick={() => setPendingPayment(null)} style={{ background: 'none', border: 'none', color: C.green, fontWeight: 600, fontSize: 14, cursor: 'pointer', marginBottom: 16, padding: 0 }}>← Back to plans</button>
                <SubscriptionPaymentForm
                    clientSecret={pendingPayment.clientSecret}
                    planName={pendingPayment.planName}
                    amount={pendingPayment.amount}
                    onSuccess={onPaymentSuccess}
                    onCancel={() => setPendingPayment(null)}
                />
            </div>
        );
    }

    if (subSuccess) {
        return (
            <div style={{ maxWidth: 520, textAlign: 'center', padding: 48 }}>
                <div style={{ fontSize: 52 }}>✅</div>
                <h2 style={{ marginTop: 16, color: C.green }}>Subscribed!</h2>
                <p style={{ color: C.textSub }}>Your plan is now active. Covered {svcLabelPlural} will be billed through your plan automatically.</p>
                <Btn onClick={onBack} style={{ marginTop: 24 }}>Back to profile</Btn>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: 680 }}>
            <h2 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 700 }}>Subscription Plans</h2>
            <p style={{ margin: '0 0 24px', color: C.textSub, fontSize: 14 }}>Save on every {svcLabel} with a plan. Cancel anytime.</p>

            {activeSub && (
                <Card style={{ marginBottom: 24, borderLeft: `4px solid ${C.green}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: 16 }}>Active: {activeSub.planName}</div>
                            <div style={{ color: C.textSub, fontSize: 13, marginTop: 4 }}>
                                Renews {new Date(activeSub.currentPeriodEnd).toLocaleDateString()}
                                {activeSub.includedPickups && ` · ${activeSub.pickupsRemaining ?? 0} ${activeSub.serviceLabel} left`}
                            </div>
                            {activeSub.willCancelAtPeriodEnd && <div style={{ marginTop: 8 }}><Alert type="error" message="Scheduled to cancel at period end" /></div>}
                        </div>
                        {!activeSub.willCancelAtPeriodEnd && (
                            <Btn variant="danger" onClick={cancel} disabled={cancelling}>
                                {cancelling ? 'Cancelling…' : 'Cancel plan'}
                            </Btn>
                        )}
                    </div>
                </Card>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
                {plans.map(plan => {
                    const isCurrent = activeSub?.planId === plan.id;
                    return (
                        <Card key={plan.id} style={{ position: 'relative', border: isCurrent ? `2px solid ${C.green}` : undefined }}>
                            {isCurrent && <div style={{ position: 'absolute', top: 10, right: 10, background: C.green, color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99 }}>Current</div>}
                            <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 4 }}>{plan.name}</div>
                            <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 8 }}>${plan.monthlyPrice}<span style={{ fontSize: 13, fontWeight: 400, color: C.textSub }}>{billingPeriodSuffix(plan.billingInterval)}</span></div>
                            {plan.description && <div style={{ fontSize: 13, color: C.textSub, marginBottom: 12 }}>{plan.description}</div>}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, marginBottom: 16 }}>
                                {plan.includedPickups ? <span>📦 {plan.includedPickups} {plan.serviceLabel}</span> : <span>📦 Unlimited {plan.serviceLabel}</span>}
                                {plan.discountPercent > 0 && <span>💰 {plan.discountPercent}% off each {svcLabel}</span>}
                                {plan.priorityScheduling && <span>⚡ Priority scheduling</span>}
                            </div>
                            <Btn
                                fullWidth
                                variant={isCurrent ? 'outline' : 'primary'}
                                disabled={isCurrent || !!subscribing || !!switching || (!!activeSub && activeSub.willCancelAtPeriodEnd)}
                                onClick={() => activeSub && !isCurrent ? changePlan(plan) : subscribe(plan)}
                            >
                                {switching === plan.id ? 'Switching…'
                                    : subscribing === plan.id ? 'Setting up…'
                                    : isCurrent ? 'Current plan'
                                    : activeSub && activeSub.willCancelAtPeriodEnd ? 'Cancelling'
                                    : activeSub ? (plan.monthlyPrice > activeSub.monthlyPrice ? 'Upgrade' : 'Downgrade')
                                    : 'Subscribe'}
                            </Btn>
                        </Card>
                    );
                })}
            </div>
            {plans.length === 0 && <div style={{ color: C.textSub, textAlign: 'center', padding: 40 }}>No plans available right now.</div>}
        </div>
    );
}

// ── Jobs list ─────────────────────────────────────────────────────────────────

function JobsList({ jobs, loading, onTrack, onRefresh, onCancel }: {
    jobs: Job[];
    loading: boolean;
    onTrack: (j: Job) => void;
    onRefresh: () => void;
    onCancel: (j: Job) => void;
}) {
    const branding = useBranding();
    const svcLabel = branding.serviceNoun ?? 'service';
    const svcLabelPlural = branding.serviceNoun ? branding.serviceNoun + 's' : 'services';
    const [ratingJob, setRatingJob] = useState<Job | null>(null);

    useEffect(() => {
        const hasActive = jobs.some(j => !['Completed', 'Cancelled'].includes(j.status));
        if (!hasActive) return;
        const interval = setInterval(onRefresh, 10000);
        return () => clearInterval(interval);
    }, [jobs, onRefresh]);

    if (loading) return <Spinner />;

    const active = jobs.filter(j => !['Completed', 'Cancelled'].includes(j.status));
    const past = jobs.filter(j => ['Completed', 'Cancelled'].includes(j.status));

    return (
        <div>
            {ratingJob && <RatingModal job={ratingJob} onClose={() => { setRatingJob(null); onRefresh(); }} />}
            <div style={{ display: 'flex', gap: 16, marginBottom: 28, flexWrap: 'wrap' }}>
                <StatCard label={`Total ${svcLabelPlural}`} value={jobs.length} />
                <StatCard label="Completed" value={jobs.filter(j => j.status === 'Completed').length} color={C.greenMid} />
            </div>
            {active.length > 0 && (
                <>
                    <h3 style={{ margin: '0 0 14px', fontSize: 15, color: C.textSub, textTransform: 'uppercase', letterSpacing: 0.5 }}>Active</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 28 }}>
                        {active.map(j => (
                            <JobCard
                                key={j.id}
                                job={j}
                                onTrack={() => onTrack(j)}
                                onCancel={() => onCancel(j)}
                                onRefresh={onRefresh}
                            />
                        ))}
                    </div>
                </>
            )}
            {past.length > 0 && (
                <>
                    <h3 style={{ margin: '0 0 14px', fontSize: 15, color: C.textSub, textTransform: 'uppercase', letterSpacing: 0.5 }}>Past {svcLabelPlural}</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {past.map(j => (
                            <JobCard
                                key={j.id}
                                job={j}
                                onRate={j.status === 'Completed' && !j.rating ? () => setRatingJob(j) : undefined}
                                onRefresh={onRefresh}
                            />
                        ))}
                    </div>
                </>
            )}
            {jobs.length === 0 && (
                <Card style={{ textAlign: 'center', padding: 48 }}>
                    <div style={{ fontSize: 40 }}>📦</div>
                    <div style={{ fontSize: 16, fontWeight: 600, marginTop: 12 }}>No {svcLabelPlural} yet</div>
                    <div style={{ color: C.textSub, marginTop: 6 }}>Click "+ Services" to book your first {svcLabel} or rent a dumpster.</div>
                </Card>
            )}
        </div>
    );
}

function JobCard({ job, onTrack, onRate, onCancel, onRefresh }: {
    job: Job;
    onTrack?: () => void;
    onRate?: () => void;
    onCancel?: () => void;
    onRefresh?: () => void;
}) {
    const canCancel = onCancel && !['Completed', 'Cancelled'].includes(job.status);
    // A job is created up front (see confirmBooking) so its PaymentIntent has something to attach
    // to — but if the customer navigates away before the Stripe payment form actually confirms
    // (closed tab, browser back), the job is left behind exactly like this: real, sitting in the
    // customer's own job list forever, paymentStatus still Pending. It's harmless to anyone but
    // the customer — GetNearbyJobsAsync only surfaces Authorized jobs, so no contractor ever sees
    // or accepts it — but it looked identical to a normal "waiting for a contractor" job, with
    // nothing explaining why no contractor ever came. subscriptionCovered jobs skip Stripe
    // entirely and are Authorized immediately, so they never match this.
    // A cancelled (or completed) job keeps paymentStatus 'Pending' since it was never authorized —
    // without the status guard, "Payment required" + "Complete Payment" kept showing on a job the
    // customer already cancelled.
    const needsPayment = job.paymentStatus === 'Pending'
        && !job.subscriptionCovered
        && !['Completed', 'Cancelled'].includes(job.status);
    const [resumeClientSecret, setResumeClientSecret] = useState<string | null>(null);
    const [resumingPayment, setResumingPayment] = useState(false);
    const startResumePayment = async () => {
        setResumingPayment(true);
        try {
            const intent = await api.createPaymentIntent(job.id);
            setResumeClientSecret(intent.clientSecret);
        } catch (e: any) { alert(e.message); }
        finally { setResumingPayment(false); }
    };
    const chatEnabled = useFeature('inAppChat');
    const partialPaymentsEnabled = useFeature('partialPayments');
    const digitalWorkOrdersEnabled = useFeature('digitalWorkOrders');
    const estimatesEnabled = useFeature('estimatesAndQuotes');
    const [adjustment, setAdjustment] = useState<any>(null);
    const [showAdjustment, setShowAdjustment] = useState(false);
    const [showChat, setShowChat] = useState(false);
    const [invoice, setInvoice] = useState<InvoiceDto | null>(null);
    const [workOrder, setWorkOrder] = useState<WorkOrderDto | null>(null);
    const [pendingEstimate, setPendingEstimate] = useState<EstimateDto | null>(null);
    const [estimateLineItems, setEstimateLineItems] = useState<JobLineItemDto[]>([]);
    const [showEstimate, setShowEstimate] = useState(false);
    const [respondingEarlyStart, setRespondingEarlyStart] = useState(false);
    const respondEarlyStart = async (approve: boolean) => {
        setRespondingEarlyStart(true);
        try { await api.respondToEarlyStart(job.id, approve); onRefresh?.(); }
        finally { setRespondingEarlyStart(false); }
    };

    // TREK-78 follow-up: previously only visible from the live-tracking screen — now
    // surfaced directly on the activity card too, same poll pattern as tracking used.
    useEffect(() => {
        if (!estimatesEnabled || job.status !== 'InProgress') { setPendingEstimate(null); return; }
        let cancelled = false;
        const check = () => {
            api.getEstimate(job.id).then(est => {
                if (cancelled) return;
                if (est?.status === 'Pending') {
                    setPendingEstimate(est);
                    api.getJobLineItems(job.id).then(summary => { if (!cancelled) setEstimateLineItems(summary.lineItems); }).catch(() => {});
                } else {
                    setPendingEstimate(null);
                }
            });
        };
        check();
        const interval = setInterval(check, 8000);
        return () => { cancelled = true; clearInterval(interval); };
    }, [estimatesEnabled, job.status, job.id]);

    // Fetch adjustment details if pending
    useEffect(() => {
        if (job.priceAdjustmentStatus === 'Pending') {
            const token = localStorage.getItem('br_token');
            fetch(`${API_BASE}/api/price-adjustment/${job.id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            })
                .then(r => r.ok ? r.json() : null)
                .then(data => { if (data) setAdjustment(data); })
                .catch(() => {});
        }
    }, [job.id, job.priceAdjustmentStatus]);

    // TREK-79: invoice is auto-generated on completion — pick it up once the job is done
    useEffect(() => {
        if (job.status === 'Completed') {
            api.getInvoice(job.id).then(setInvoice).catch(() => {});
        }
    }, [job.id, job.status]);

    // TREK-88: work order exists once a contractor starts the job (InProgress or later)
    useEffect(() => {
        if (digitalWorkOrdersEnabled && !['Pending', 'Accepted', 'EnRoute', 'Arrived'].includes(job.status)) {
            api.getWorkOrder(job.id).then(setWorkOrder).catch(() => {});
        }
    }, [job.id, job.status, digitalWorkOrdersEnabled]);

    return (
        <>
            {chatEnabled && showChat && <JobChat jobId={job.id} onClose={() => setShowChat(false)} />}
            {resumeClientSecret && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}>
                    <div style={{ width: '100%', maxWidth: 420 }}>
                        <PaymentForm
                            jobId={job.id}
                            amount={job.grandTotal ?? job.totalPrice}
                            clientSecret={resumeClientSecret}
                            onSuccess={async () => {
                                await confirmAuthWithRetry(() => api.confirmJobAuthorization(job.id), "job");
                                setResumeClientSecret(null);
                                onRefresh?.();
                            }}
                            onCancel={() => setResumeClientSecret(null)}
                        />
                    </div>
                </div>
            )}
            {showEstimate && pendingEstimate && (
                <EstimateApprovalModal
                    jobId={job.id}
                    estimate={pendingEstimate}
                    lineItems={estimateLineItems}
                    onResolved={() => { setShowEstimate(false); setPendingEstimate(null); onRefresh?.(); }}
                />
            )}
            {showAdjustment && adjustment && (
                <PriceAdjustmentModal
                    jobId={job.id}
                    estimatedUnits={adjustment.estimatedUnits}
                    actualUnits={adjustment.actualUnits}
                    originalPrice={adjustment.originalPrice}
                    adjustedPrice={adjustment.adjustedPrice}
                    serviceFeeAmount={adjustment.serviceFeeAmount}
                    taxAmount={adjustment.taxAmount}
                    contractorNotes={adjustment.notes}
                    onApprove={() => { setShowAdjustment(false); onRefresh?.(); }}
                    onDispute={() => { setShowAdjustment(false); onRefresh?.(); }}
                />
            )}
            <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                            <Badge label={job.serviceType} />
                            <Badge label={job.status} />
                            {job.priceAdjustmentStatus === 'Pending' && (
                                <span style={{ background: '#FAEEDA', color: '#854F0B', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                                    ⚠ Price adjustment pending
                                </span>
                            )}
                            {job.priceAdjustmentStatus === 'Disputed' && (
                                <span style={{ background: '#FDECEA', color: '#9B1C1C', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                                    🔍 Under review
                                </span>
                            )}
                            {pendingEstimate && (
                                <span style={{ background: '#FAEEDA', color: '#854F0B', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                                    ⏳ Estimate awaiting approval
                                </span>
                            )}
                            {job.earlyStartStatus === 'Requested' && (
                                <span style={{ background: '#EEF2FF', color: '#4338CA', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                                    🕐 Early start requested
                                </span>
                            )}
                            {needsPayment && (
                                <span style={{ background: '#FDECEA', color: C.coral, borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                                    ⚠ Payment required
                                </span>
                            )}
                        </div>
                        <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>{job.pickupAddress}</div>
                        <div style={{ fontSize: 13, color: C.textSub }}>
                            {job.estimatedUnits} units · ${(job.grandTotal ?? job.totalPrice).toFixed(2)}
                            {job.scheduledTimeSlot && ` · ${job.scheduledTimeSlot}`}
                        </div>
                        {job.contractor && (
                            <div style={{ fontSize: 13, color: C.greenMid, marginTop: 6 }}>
                                🚚 {job.contractor.name} · ★ {job.contractor.averageRating.toFixed(1)}
                            </div>
                        )}
                        {job.rating && <div style={{ marginTop: 6 }}><StarRating value={job.rating.stars} /></div>}
                        {needsPayment && (
                            <div style={{ marginTop: 8, padding: 10, background: '#FDECEA', borderRadius: 8, fontSize: 13, color: '#9B1C1C' }}>
                                Payment wasn't completed for this pickup, so no contractor has been assigned yet. Complete payment to place a temporary authorization hold — not a charge — or cancel. You're only charged once the pickup is done.
                            </div>
                        )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
                        <span style={{ fontSize: 12, color: C.textMuted }}>{new Date(job.createdAt).toLocaleDateString()}</span>
                        {needsPayment && (
                            <Btn size="sm" variant="danger" onClick={startResumePayment} disabled={resumingPayment}>
                                {resumingPayment ? 'Loading...' : '💳 Complete Payment'}
                            </Btn>
                        )}
                        {onTrack && <Btn size="sm" onClick={onTrack}>📍 Track</Btn>}
                        {chatEnabled && job.contractor && !['Completed', 'Cancelled'].includes(job.status) && (
                            <Btn size="sm" variant="outline" onClick={() => setShowChat(true)}>💬 Chat</Btn>
                        )}
                        {onRate && <Btn size="sm" variant="outline" onClick={onRate}>⭐ Rate</Btn>}
                        {invoice && (
                            <Btn size="sm" variant="outline" onClick={() => api.downloadInvoicePdf(job.id, invoice.number)}>📄 Invoice</Btn>
                        )}
                        {workOrder?.status === 'Signed' && workOrder.signedPdfUrl && (
                            <Btn size="sm" variant="outline" onClick={() => window.open(workOrder.signedPdfUrl!, '_blank')}>🖊 Work Order</Btn>
                        )}
                        {canCancel && <Btn size="sm" variant="danger" onClick={onCancel}>✕ Cancel</Btn>}
                        {job.priceAdjustmentStatus === 'Pending' && adjustment && (
                            <Btn size="sm" onClick={() => setShowAdjustment(true)}>
                                💰 Review charge
                            </Btn>
                        )}
                        {pendingEstimate && (
                            <Btn size="sm" onClick={() => setShowEstimate(true)}>
                                ⏳ Review estimate
                            </Btn>
                        )}
                    </div>
                </div>
                {job.earlyStartStatus === 'Requested' && (
                    <div style={{ marginTop: 12, padding: 10, background: '#EEF2FF', borderRadius: 8 }}>
                        <div style={{ fontSize: 13, color: '#3730A3', marginBottom: 8 }}>
                            {job.contractor?.name ?? 'Your runner'} would like to start before {job.scheduledDate ? new Date(job.scheduledDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'the scheduled date'}.
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Btn size="sm" onClick={() => respondEarlyStart(true)} disabled={respondingEarlyStart}>✅ Allow</Btn>
                            <Btn size="sm" variant="outline" onClick={() => respondEarlyStart(false)} disabled={respondingEarlyStart}>Not yet</Btn>
                        </div>
                    </div>
                )}
            </Card>
            {partialPaymentsEnabled && <PaymentScheduleCard jobId={job.id} />}
        </>
    );
}

// ── Payment schedule (TREK-80) — renders only when the job was split into milestones ──

function PaymentScheduleCard({ jobId }: { jobId: string }) {
    const [schedule, setSchedule] = useState<PaymentScheduleDto | null>(null);
    const [payingMilestoneId, setPayingMilestoneId] = useState<string | null>(null);
    const [intent, setIntent] = useState<{ clientSecret: string; amount: number; label: string } | null>(null);
    const [error, setError] = useState('');

    const load = useCallback(() => {
        api.getPaymentSchedule(jobId).then(setSchedule).catch(() => {});
    }, [jobId]);

    useEffect(() => { load(); }, [load]);

    if (!schedule || schedule.milestones.length === 0) return null;

    const nextDue = schedule.milestones.find(m => m.status === 'Pending');

    const startPayment = async (milestoneId: string, label: string, amount: number) => {
        setError('');
        setPayingMilestoneId(milestoneId);
        try {
            const res = await api.createMilestoneIntent(jobId, milestoneId);
            setIntent({ clientSecret: res.clientSecret, amount: res.amount, label });
        } catch (e: any) {
            setError(e.message || 'Could not start payment');
            setPayingMilestoneId(null);
        }
    };

    const onPaymentSuccess = async () => {
        if (!payingMilestoneId) return;
        try { await api.confirmMilestoneCapture(jobId, payingMilestoneId); }
        catch (e: any) { setError(e.message || 'Payment succeeded but confirmation failed — refresh to check status.'); }
        setIntent(null);
        setPayingMilestoneId(null);
        load();
    };

    return (
        <Card style={{ marginTop: -4 }}>
            <h4 style={{ margin: '0 0 4px', fontSize: 14 }}>Payment Schedule</h4>
            <div style={{ fontSize: 12, color: C.textSub, marginBottom: 10 }}>
                Total job: ${schedule.totalAmount.toFixed(2)} · Remaining: ${schedule.remainingBalance.toFixed(2)}
            </div>
            <Alert message={error} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {schedule.milestones.map(m => (
                    <div key={m.id} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '8px 10px', borderRadius: 8, border: `1px solid ${C.border}`,
                        background: m.status === 'Captured' ? '#F0FBF6' : '#fff',
                    }}>
                        <span style={{ fontSize: 13 }}>
                            {m.status === 'Captured' ? '✓ ' : ''}{m.label}
                            {m.dueDescription && <span style={{ color: C.textMuted }}> — {m.dueDescription}</span>}
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>${m.amount.toFixed(2)}</span>
                    </div>
                ))}
            </div>
            {nextDue && (
                <Btn
                    size="sm"
                    fullWidth
                    style={{ marginTop: 10 }}
                    onClick={() => startPayment(nextDue.id, nextDue.label, nextDue.amount)}
                    disabled={payingMilestoneId === nextDue.id && !intent}
                >
                    Pay {nextDue.label} Milestone — ${nextDue.amount.toFixed(2)}
                </Btn>
            )}
            {intent && (
                <div style={{ marginTop: 14 }}>
                    <MilestonePaymentForm
                        clientSecret={intent.clientSecret}
                        label={intent.label}
                        amount={intent.amount}
                        onSuccess={onPaymentSuccess}
                        onCancel={() => { setIntent(null); setPayingMilestoneId(null); }}
                    />
                </div>
            )}
        </Card>
    );
}

// ── Dumpster Orders Section (Activity tab) ────────────────────────────────────

function DumpsterOrdersSection() {
    const loadingJobsEnabled = useFeature('loadingJobs');
    const [orders, setOrders] = useState<any[]>([]);
    const [runnerLocations, setRunnerLocations] = useState<Record<string, any>>({});
    const [trackingLoadingJobId, setTrackingLoadingJobId] = useState<string | null>(null);
    const [editingOrder, setEditingOrder] = useState<any>(null);
    const [editForm, setEditForm] = useState<any>({});
    // Same gap as job booking (see CustomerPortal's JobCard needsPayment/startResumePayment) — an
    // order created by bookDumpster before the payment sheet resolves is left at
    // DumpsterOrderStatus.PaymentPending forever if the customer navigates away mid-payment. That
    // status is a dedicated, never-reused terminal-safe value, so a plain status check is enough.
    const [resumeOrderId, setResumeOrderId] = useState<string | null>(null);
    const [resumeClientSecret, setResumeClientSecret] = useState<string | null>(null);
    const [resumingPayment, setResumingPayment] = useState(false);
    const startResumeOrderPayment = async (orderId: string) => {
        setResumingPayment(true);
        try {
            const intent = await api.getDumpsterPaymentIntent(orderId);
            setResumeOrderId(orderId);
            setResumeClientSecret(intent.clientSecret);
        } catch (e: any) { alert(e.message); }
        finally { setResumingPayment(false); }
    };
    const hubRef = useRef<signalR.HubConnection | null>(null);
    const subscribedRef = useRef<Set<string>>(new Set());
    const ordersRef = useRef<any[]>([]);

    const subscribeToOrders = useCallback((orderList: any[]) => {
        const hub = hubRef.current;
        if (!hub || hub.state !== signalR.HubConnectionState.Connected) return;
        orderList.forEach(o => {
            if (!subscribedRef.current.has(o.id)) {
                hub.invoke('SubscribeToDumpsterOrder', o.id)
                    .then(() => subscribedRef.current.add(o.id))
                    .catch(() => {});
            }
        });
    }, []);

    const loadOrders = useCallback(async () => {
        try {
            const data = await api.getMyDumpsterOrders();
            setOrders(data);
        } catch { }
    }, []);

    useEffect(() => {
        loadOrders();
        const token = localStorage.getItem('br_token');
        const hub = new signalR.HubConnectionBuilder()
            .withUrl(`${API_BASE}/hubs/tracking?access_token=${token}`, { withCredentials: false })
            .withAutomaticReconnect()
            .build();
        hub.on('DumpsterOrderUpdated', () => { loadOrders(); });
        hub.on('DumpsterRunnerLocationUpdated', (data: any) => {
            setRunnerLocations(prev => ({ ...prev, [data.loadingJobId]: data }));
        });
        hub.start().then(() => subscribeToOrders(ordersRef.current)).catch(console.error);
        hub.onreconnected(() => { subscribedRef.current.clear(); subscribeToOrders(ordersRef.current); });
        hubRef.current = hub;
        return () => { hub.stop(); };
    }, [loadOrders, subscribeToOrders]);

    useEffect(() => {
        ordersRef.current = orders;
        subscribeToOrders(orders);
    }, [orders, subscribeToOrders]);

    const cancelOrder = async (orderId: string) => {
        if (!window.confirm('Cancel this dumpster order? This cannot be undone.')) return;
        try { await api.cancelDumpsterOrder(orderId); await loadOrders(); } catch (e: any) { alert(e.message); }
    };
    const requestPickup = async (orderId: string) => {
        try { await api.requestDumpsterPickup(orderId, ''); await loadOrders(); } catch (e: any) { alert(e.message); }
    };
    const saveEdit = async () => {
        if (!editingOrder) return;
        try { await api.updateDumpsterOrder(editingOrder.id, editForm); setEditingOrder(null); await loadOrders(); } catch (e: any) { alert(e.message); }
    };

    const statusColor = (s: string) => {
        switch (s) {
            case 'Completed': return C.green;
            case 'Delivered': return C.greenMid;
            case 'VendorAccepted': case 'DeliveryScheduled': return '#3730A3';
            case 'PickupRequested': case 'PickupScheduled': return '#854F0B';
            case 'Cancelled': return C.coral;
            default: return C.textMuted;
        }
    };
    const statusLabel = (s: string) => ({
        PaymentPending: '💳 Awaiting payment', Pending: '⏳ Awaiting vendor', VendorAccepted: '✓ Confirmed',
        DeliveryScheduled: '🚛 Delivery scheduled', Delivered: '📦 On site',
        LoadingInProgress: '💪 Loading in progress', PickupRequested: '🔔 Pickup requested',
        PickupScheduled: '🚛 Pickup scheduled', Completed: '✅ Completed', Cancelled: '✗ Cancelled',
    }[s] || s);

    if (orders.length === 0) return null;

    return (
        <div style={{ marginTop: 40 }}>
            {resumeClientSecret && resumeOrderId && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}>
                    <div style={{ width: '100%', maxWidth: 420 }}>
                        <PaymentForm
                            jobId={resumeOrderId}
                            amount={orders.find(o => o.id === resumeOrderId)?.totalCost ?? 0}
                            clientSecret={resumeClientSecret}
                            onSuccess={async () => {
                                await confirmAuthWithRetry(() => api.confirmDumpsterAuthorization(resumeOrderId), "dumpster");
                                setResumeClientSecret(null); setResumeOrderId(null);
                                loadOrders();
                            }}
                            onCancel={() => { setResumeClientSecret(null); setResumeOrderId(null); }}
                        />
                    </div>
                </div>
            )}
            <h3 style={{ margin: '0 0 14px', fontSize: 15, color: C.textSub, textTransform: 'uppercase', letterSpacing: 0.5 }}>Dumpster Orders</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {orders.map((order: any) => (
                    <Card key={order.id} style={{ borderLeft: `4px solid ${statusColor(order.status)}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                                    <span style={{ fontWeight: 700, fontSize: 16 }}>🚛 {order.dumpsterYards}yd Dumpster</span>
                                    <span style={{ fontSize: 12, fontWeight: 600, color: statusColor(order.status), background: `${statusColor(order.status)}18`, padding: '3px 10px', borderRadius: 20 }}>
                                        {statusLabel(order.status)}
                                    </span>
                                </div>
                                <div style={{ fontSize: 13, color: C.textSub, marginBottom: 4 }}>📍 {order.deliveryAddress}</div>
                                <div style={{ fontSize: 13, color: C.textSub, marginBottom: 4 }}>🚛 {order.vendorName}</div>
                                <div style={{ fontSize: 13, color: C.textSub, marginBottom: 4 }}>
                                    📅 Delivery: {new Date(order.requestedDeliveryDate).toLocaleDateString()} · {order.rentalDays} day rental
                                </div>
                                {order.rentalStartedAt && order.rentalEndsAt && (
                                    <div style={{ fontSize: 13, color: C.textSub, marginBottom: 4 }}>
                                        ⏱ Rental: {new Date(order.rentalStartedAt).toLocaleDateString()} – {new Date(order.rentalEndsAt).toLocaleDateString()}
                                    </div>
                                )}
                                {order.loadingRequested && (
                                    <div style={{ fontSize: 13, color: C.textSub }}>
                                        💪 {order.loadingRunnersRequested} loading runner{order.loadingRunnersRequested > 1 ? 's' : ''} requested
                                    </div>
                                )}
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <div style={{ fontWeight: 800, fontSize: 20, color: C.greenMid }}>${order.totalCost.toFixed(2)}</div>
                                <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 12 }}>
                                    ${order.deliveryPrice.toFixed(2)} delivery + ${order.totalRentalCost.toFixed(2)} rental
                                    {order.loadingCost > 0 ? ` + $${order.loadingCost.toFixed(2)} loading` : ''}
                                    {order.overageCost > 0 ? ` + $${order.overageCost.toFixed(2)} overage` : ''}
                                    {order.taxAmount > 0 ? ` + $${order.taxAmount.toFixed(2)} tax` : ''}
                                </div>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                    {order.status === 'PaymentPending' && (
                                        <Btn size="sm" variant="danger" onClick={() => startResumeOrderPayment(order.id)} disabled={resumingPayment}>
                                            💳 Complete Payment
                                        </Btn>
                                    )}
                                    {order.status === 'Delivered' && (() => {
                                        const hasActiveLoaders = order.loadingJobs?.some((j: any) => !['Completed', 'Cancelled'].includes(j.status));
                                        return hasActiveLoaders ? (
                                            <div style={{ fontSize: 12, color: '#854F0B', background: '#FFF7E6', borderRadius: 6, padding: '6px 10px' }}>
                                                ⏳ Waiting for loading runners to finish
                                            </div>
                                        ) : (
                                            <Btn size="sm" onClick={() => requestPickup(order.id)}>Request pickup</Btn>
                                        );
                                    })()}
                                    {['Pending', 'VendorAccepted'].includes(order.status) && (
                                        <Btn size="sm" variant="outline" onClick={() => {
                                            setEditingOrder(order);
                                            setEditForm({ requestedDeliveryDate: order.requestedDeliveryDate?.split('T')[0], rentalDays: order.rentalDays, loadingRequested: order.loadingRequested, loadingRunnersRequested: order.loadingRunnersRequested });
                                        }}>✏️ Edit</Btn>
                                    )}
                                    {!['Delivered', 'LoadingInProgress', 'PickupRequested', 'PickupScheduled', 'Completed', 'Cancelled'].includes(order.status) && (
                                        <Btn size="sm" variant="outline" onClick={() => cancelOrder(order.id)} style={{ color: C.coral, borderColor: C.coral }}>✕ Cancel</Btn>
                                    )}
                                </div>
                            </div>
                        </div>
                        {order.status !== 'Cancelled' && order.loadingJobs?.filter((j: any) => j.status !== 'Cancelled').length > 0 && (
                            <div style={{ marginTop: 16, borderTop: `1px solid ${C.border}`, paddingTop: 16 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>💪 Loading runners</div>
                                {order.loadingJobs.filter((job: any) => job.status !== 'Cancelled').map((job: any) => (
                                    <div key={job.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: C.bg, borderRadius: 8, marginBottom: 8 }}>
                                        <div>
                                            <div style={{ fontSize: 13, fontWeight: 600 }}>{job.contractorName || 'Awaiting runner'}</div>
                                            <div style={{ fontSize: 12, color: C.textSub }}>
                                                {job.status === 'InProgress' && job.clockedInAt ? `Clocked in ${new Date(job.clockedInAt).toLocaleTimeString()}` : job.status}
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            {job.hoursWorked && <div style={{ fontSize: 13, fontWeight: 600 }}>{job.hoursWorked.toFixed(2)}h · ${job.finalCost?.toFixed(2)}</div>}
                                            {job.status === 'PendingApproval' && job.contractorName && (
                                                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                                                    <Btn size="sm" onClick={async () => { await api.approveDumpsterHours(job.id, true); await loadOrders(); }}>✓ Approve</Btn>
                                                    <Btn size="sm" variant="outline" onClick={async () => { await api.approveDumpsterHours(job.id, false); await loadOrders(); }}>Dispute</Btn>
                                                </div>
                                            )}
                                            {['EnRoute', 'OnSite'].includes(job.status) && (
                                                <button onClick={() => setTrackingLoadingJobId(job.id)}
                                                    style={{ marginTop: 6, padding: '5px 12px', background: '#3730A3', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                                    📍 Track Runner
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>
                ))}
            </div>
            {trackingLoadingJobId && (() => {
                const trackOrder = orders.find((o: any) => o.loadingJobs?.some((j: any) => j.id === trackingLoadingJobId));
                const trackJob = trackOrder?.loadingJobs?.find((j: any) => j.id === trackingLoadingJobId);
                const loc = runnerLocations[trackingLoadingJobId];
                return trackOrder ? (
                    <DumpsterRunnerMapModal order={trackOrder} job={trackJob} location={loc} onClose={() => setTrackingLoadingJobId(null)} />
                ) : null;
            })()}
            {editingOrder && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
                    <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 440 }}>
                        <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 20 }}>✏️ Edit Order</div>
                        <Input label="Delivery date" value={editForm.requestedDeliveryDate || ''} onChange={(v: string) => setEditForm((f: any) => ({ ...f, requestedDeliveryDate: v }))} type="date" />
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ fontSize: 13, fontWeight: 500, color: C.textSub, display: 'block', marginBottom: 8 }}>Rental duration</label>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {[3, 5, 7, 10, 14].map(d => (
                                    <button key={d} onClick={() => setEditForm((f: any) => ({ ...f, rentalDays: d }))}
                                        style={{ padding: '8px 14px', borderRadius: 8, border: `2px solid ${editForm.rentalDays === d ? C.greenMid : C.border}`, background: editForm.rentalDays === d ? C.greenBg : '#fff', color: editForm.rentalDays === d ? C.green : C.textSub, fontWeight: editForm.rentalDays === d ? 700 : 400, fontSize: 13, cursor: 'pointer' }}>
                                        {d} days
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                            <Btn variant="outline" onClick={() => setEditingOrder(null)} fullWidth>Cancel</Btn>
                            <Btn onClick={saveEdit} fullWidth>Save changes</Btn>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Ad Carousel ───────────────────────────────────────────────────────────────

// Bronze → Silver → Gold → Premium, by ascending plan rank (sortOrder). Cycles for a 5th+ plan.
// Only the star icon varies by tier — the card itself always uses the BoxRunner brand green.
const SUBSCRIPTION_TIER_STAR_COLORS = ['#CD7F32', '#A8AEB8', '#D4A017', '#D4A017']; // Bronze, Silver, Gold, Gold (cycles beyond 3 tiers)

export function AdCarousel({ onViewPlans }: { onViewPlans?: () => void }) {
    const branding = useBranding();
    const brandButtonColor = branding.buttonColor ?? branding.primaryColor;
    const [ads, setAds] = useState<AdItem[]>([]);
    const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Independent try/catch per call, same as before — one endpoint failing shouldn't hide the
        // other's cards. allSettled just waits for both to land so we know when to stop showing the
        // skeleton (this row used to render nothing at all until both resolved, then pop the cards
        // in all at once with no warning — read as "slow" even when the calls were quick).
        Promise.allSettled([
            api.getAds().then(setAds).catch(() => {}),
            api.getSubscriptionPlans().then(setPlans).catch(() => {}),
        ]).then(() => setLoading(false));
    }, []);

    const hasContent = ads.length > 0 || plans.length > 0;
    if (loading) {
        // Same footprint as the real cards (240x280) so nothing reflows when they swap in, instead
        // of the previous blank gap that popped straight to full content — that gap is what read as
        // "slow" even on a fast connection, since there was zero feedback the row was loading at all.
        return (
            <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Offers, Updates &amp; Supporters</div>
                <div style={{ display: 'flex', gap: 14, overflowX: 'auto', paddingBottom: 6 }}>
                    {[0, 1, 2].map(i => (
                        <div key={i} style={{ flexShrink: 0, width: 240, height: 280, borderRadius: 14, background: C.card, border: `1px solid ${C.border}`, opacity: 0.5, animation: 'adPulse 1.4s ease-in-out infinite', animationDelay: `${i * 0.15}s` }} />
                    ))}
                    <style>{`@keyframes adPulse { 0%, 100% { opacity: 0.35; } 50% { opacity: 0.6; } }`}</style>
                </div>
            </div>
        );
    }
    if (!hasContent) return null;

    return (
        <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 11, color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Offers, Updates &amp; Supporters</div>
            <div style={{ display: 'flex', gap: 14, overflowX: 'auto', paddingBottom: 6, scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}>
                {/* Subscription plan cards follow the tenant's own brand colors (primary/secondary/
                    button), same as every button elsewhere in the app — only the star icon is
                    colored by tier (plans arrive sorted ascending by sortOrder from
                    GetActivePlansAsync, so array position is rank: lowest tier first). */}
                {plans.map((plan, i) => {
                    const starColor = SUBSCRIPTION_TIER_STAR_COLORS[i % SUBSCRIPTION_TIER_STAR_COLORS.length];
                    return (
                    <div key={plan.id} style={{ flexShrink: 0, width: 240, height: 280, borderRadius: 14, overflow: 'hidden', border: `2px solid ${branding.primaryColor}`, background: C.card, display: 'flex', flexDirection: 'column', scrollSnapAlign: 'start' }}>
                        <div style={{ background: `linear-gradient(135deg, ${branding.secondaryColor}, ${branding.primaryColor})`, height: 120, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                            <Star size={28} color={starColor} fill={starColor} />
                            <div style={{ color: '#fff', fontWeight: 800, fontSize: 20 }}>${plan.monthlyPrice}<span style={{ fontSize: 12, fontWeight: 400 }}>{billingPeriodSuffix(plan.billingInterval)}</span></div>
                        </div>
                        <div style={{ padding: '12px 14px 14px', flex: 1, display: 'flex', flexDirection: 'column', gap: 4, overflow: 'hidden' }}>
                            <div style={{ fontWeight: 700, fontSize: 14, color: C.text, flexShrink: 0 }}>{plan.name}</div>
                            <div style={{ overflow: 'hidden', flexShrink: 1 }}>
                                {plan.description && <div style={{ fontSize: 12, color: C.textSub, lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{plan.description}</div>}
                                {plan.includedPickups != null && <div style={{ fontSize: 11, color: C.textMuted }}>✓ {plan.includedPickups} {plan.serviceLabel} included</div>}
                                {plan.discountPercent > 0 && <div style={{ fontSize: 11, color: C.textMuted }}>✓ {plan.discountPercent}% off all pickups</div>}
                                {plan.priorityScheduling && <div style={{ fontSize: 11, color: C.textMuted }}>✓ Priority scheduling</div>}
                            </div>
                            <button onClick={onViewPlans} style={{ marginTop: 'auto', flexShrink: 0, background: brandButtonColor, color: '#fff', border: 'none', borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', textAlign: 'center' }}>
                                Subscribe →
                            </button>
                        </div>
                    </div>
                    );
                })}
                {/* Regular ad cards */}
                {ads.map(ad => (
                    <div key={ad.id} style={{ flexShrink: 0, width: 240, height: 280, borderRadius: 14, overflow: 'hidden', border: `1px solid ${C.border}`, background: C.card, display: 'flex', flexDirection: 'column', scrollSnapAlign: 'start' }}>
                        {ad.imageData ? (
                            <img src={ad.imageData} alt={ad.title} style={{ width: '100%', height: 120, objectFit: 'cover', display: 'block', flexShrink: 0 }} />
                        ) : (
                            <div style={{ width: '100%', height: 120, flexShrink: 0, background: `linear-gradient(135deg, ${branding.secondaryColor}, ${branding.primaryColor})`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <span style={{ fontSize: 36 }}>📦</span>
                            </div>
                        )}
                        <div style={{ padding: '12px 14px 14px', flex: 1, display: 'flex', flexDirection: 'column', gap: 4, overflow: 'hidden' }}>
                            <div style={{ fontWeight: 700, fontSize: 14, color: C.text, lineHeight: 1.3, flexShrink: 0 }}>{ad.title}</div>
                            {ad.subtitle && <div style={{ fontSize: 12, color: C.textSub, lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', flexShrink: 1 }}>{ad.subtitle}</div>}
                            {ad.buttonUrl && (
                                <a href={ad.buttonUrl} target="_blank" rel="noopener noreferrer" style={{ marginTop: 'auto', flexShrink: 0, paddingTop: 8, display: 'block' }}>
                                    <div style={{ background: brandButtonColor, color: '#fff', borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 700, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {ad.buttonText.slice(0, 28)}{ad.buttonText.length > 28 ? '…' : ''}
                                    </div>
                                </a>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ── Send a Gift ───────────────────────────────────────────────────────────────
// A simple, single-use, service-type-locked voucher (not a dollar-balance gift card — no partial
// redemption, no balance math). Purchase is web-only; the recipient redeems the code at checkout
// when booking, which also works from the mobile app — RedeemedJob tracking after that is
// identical to any other job on either platform.

function SendGiftFlow({ onComplete }: { onComplete: () => void }) {
    const [serviceOptions, setServiceOptions] = useState<{ value: string; label: string; requiresDocumentCategories: boolean }[]>([]);
    const [serviceType, setServiceType] = useState('');
    const [recipientName, setRecipientName] = useState('');
    const [recipientEmail, setRecipientEmail] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [purchase, setPurchase] = useState<GiftVoucherPurchaseResponse | null>(null);
    const [sent, setSent] = useState(false);
    const [shreddingRequestType, setShreddingRequestType] = useState('BinDelivery');
    const [shreddingCustomerType, setShreddingCustomerType] = useState('');
    const [shreddingSizeId, setShreddingSizeId] = useState('');
    const [shreddingUnits, setShreddingUnits] = useState('1');
    const [binSizes, setBinSizes] = useState<ShreddingBinSizeDto[]>([]);
    const [retrievalFee, setRetrievalFee] = useState(0);

    useEffect(() => {
        api.getServiceTypes().then(types => {
            const filtered = types.filter(t => t.value.toLowerCase() !== 'dumpster' && t.allowGifting);
            setServiceOptions(filtered.map(t => ({ value: t.value, label: t.label, requiresDocumentCategories: t.requiresDocumentCategories })));
            if (filtered.length > 0) setServiceType(filtered[0].value);
        }).catch(() => {});
    }, []);

    const selectedOption = serviceOptions.find(s => s.value === serviceType);
    const documentCategoriesRequired = selectedOption?.requiresDocumentCategories ?? false;

    useEffect(() => {
        setShreddingRequestType('BinDelivery'); setShreddingCustomerType(''); setShreddingSizeId(''); setShreddingUnits('1'); setBinSizes([]);
        if (!documentCategoriesRequired) return;
        api.getShreddingBinSizes().then(setBinSizes).catch(() => setBinSizes([]));
        api.getMyShreddingBinStatus().then(s => setRetrievalFee(s.retrievalFee)).catch(() => setRetrievalFee(0));
    }, [documentCategoriesRequired, serviceType]);

    const shreddingSizesForCustomerType = binSizes.filter(s => s.isActive &&
        (shreddingCustomerType === 'Business' ? s.availableToBusiness : s.availableToResidential));
    const isBinDelivery = shreddingRequestType === 'BinDelivery';

    const startPurchase = async () => {
        if (!serviceType) { setError('Please select a service type.'); return; }
        if (!recipientName.trim()) { setError("Please enter the recipient's name."); return; }
        if (!recipientEmail.trim()) { setError("Please enter the recipient's email."); return; }
        if (documentCategoriesRequired) {
            if (!shreddingCustomerType) { setError('Please select residential or business.'); return; }
            if (isBinDelivery && !shreddingSizeId) { setError('Please select a bin size.'); return; }
        }
        setError(''); setLoading(true);
        try {
            const p = await api.purchaseGiftVoucher({
                serviceType, recipientName: recipientName.trim(), recipientEmail: recipientEmail.trim(), message: message.trim() || undefined,
                shreddingSizeId: documentCategoriesRequired && isBinDelivery ? shreddingSizeId : undefined,
                shreddingCustomerType: documentCategoriesRequired ? shreddingCustomerType : undefined,
                shreddingRequestType: documentCategoriesRequired ? shreddingRequestType : undefined,
                shreddingEstimatedUnits: documentCategoriesRequired && isBinDelivery ? (parseInt(shreddingUnits) || 1) : undefined,
            });
            setPurchase(p);
        } catch (e: any) { setError(e.message); }
        finally { setLoading(false); }
    };

    if (sent) {
        return (
            <Card>
                <h2 style={{ margin: '0 0 10px', fontSize: 18 }}>🎁 Gift sent!</h2>
                <p style={{ color: C.textSub, fontSize: 14 }}>
                    We emailed {recipientName} their gift voucher code at {recipientEmail}. They can redeem it at checkout the next time they book a {serviceOptions.find(s => s.value === serviceType)?.label} pickup.
                </p>
                <Btn onClick={onComplete}>Done</Btn>
            </Card>
        );
    }

    if (purchase) {
        return (
            <MilestonePaymentForm
                clientSecret={purchase.clientSecret}
                label="Gift Voucher"
                amount={purchase.amount}
                onSuccess={async () => {
                    try { await api.confirmGiftVoucherPurchase(purchase.paymentIntentId); setSent(true); }
                    catch (e: any) { setError(e.message); }
                }}
                onCancel={() => setPurchase(null)}
            />
        );
    }

    return (
        <Card>
            <h2 style={{ margin: '0 0 6px', fontSize: 18 }}>🎁 Send a Gift</h2>
            <p style={{ color: C.textSub, fontSize: 13, margin: '0 0 20px' }}>
                Buy a pickup for someone else — pay now, we'll email them a code to redeem whenever they're ready.
            </p>
            <Alert message={error} />
            <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 500, color: C.textSub, display: 'block', marginBottom: 6 }}>Service type</label>
                <select value={serviceType} onChange={e => setServiceType(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, boxSizing: 'border-box' }}>
                    {serviceOptions.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
            </div>
            {documentCategoriesRequired && (
                <div style={{ marginBottom: 14 }}>
                    <label style={{ fontSize: 12, fontWeight: 500, color: C.textSub, display: 'block', marginBottom: 6 }}>Residential or business?</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                        {['Residential', 'Business'].map(opt => (
                            <button key={opt} type="button" onClick={() => { setShreddingCustomerType(opt); setShreddingSizeId(''); }}
                                style={{ flex: 1, padding: '9px 12px', borderRadius: 8, border: `2px solid ${shreddingCustomerType === opt ? C.greenMid : C.border}`, background: shreddingCustomerType === opt ? C.greenBg : '#fff', color: shreddingCustomerType === opt ? C.green : C.text, fontWeight: 600, fontSize: 13.5, cursor: 'pointer' }}>
                                {opt}
                            </button>
                        ))}
                    </div>
                </div>
            )}
            {documentCategoriesRequired && (
                <div style={{ marginBottom: 14 }}>
                    <label style={{ fontSize: 12, fontWeight: 500, color: C.textSub, display: 'block', marginBottom: 6 }}>What are you gifting?</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {[
                            { value: 'BinDelivery', label: 'A new bin, delivered', desc: "They don't have a bin yet — this gifts a fresh delivery." },
                            { value: 'BinRetrieval', label: 'A bin pickup', desc: "They already have a bin and just need it picked up — flat fee, any size." },
                        ].map(opt => (
                            <button key={opt.value} type="button" onClick={() => setShreddingRequestType(opt.value)}
                                style={{ textAlign: 'left', padding: '10px 14px', borderRadius: 10, border: `2px solid ${shreddingRequestType === opt.value ? C.greenMid : C.border}`, background: shreddingRequestType === opt.value ? C.greenBg : '#fff', cursor: 'pointer' }}>
                                <div style={{ fontSize: 13.5, fontWeight: shreddingRequestType === opt.value ? 700 : 600, color: shreddingRequestType === opt.value ? C.green : C.text }}>{opt.label}</div>
                                <div style={{ fontSize: 12, color: C.textSub, marginTop: 2 }}>{opt.desc}</div>
                            </button>
                        ))}
                    </div>
                    <div style={{ fontSize: 11.5, color: C.textMuted, marginTop: 6 }}>
                        A destruction-service visit for an existing bin can't be gifted in advance — its price depends on the bin already on file, which isn't known until they book.
                    </div>
                </div>
            )}
            {documentCategoriesRequired && isBinDelivery && shreddingCustomerType && (
                <div style={{ marginBottom: 14 }}>
                    <label style={{ fontSize: 12, fontWeight: 500, color: C.textSub, display: 'block', marginBottom: 6 }}>Bin size</label>
                    {shreddingSizesForCustomerType.length === 0 ? (
                        <div style={{ fontSize: 13, color: C.textSub }}>No bin sizes are set up yet — contact support.</div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {shreddingSizesForCustomerType.map(size => (
                                <button key={size.id} type="button" onClick={() => setShreddingSizeId(size.id)}
                                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'left', padding: '10px 14px', borderRadius: 10, border: `2px solid ${shreddingSizeId === size.id ? C.greenMid : C.border}`, background: shreddingSizeId === size.id ? C.greenBg : '#fff', cursor: 'pointer' }}>
                                    <span style={{ fontSize: 13.5, fontWeight: shreddingSizeId === size.id ? 700 : 600, color: shreddingSizeId === size.id ? C.green : C.text }}>{size.name}</span>
                                    <span style={{ fontSize: 14, fontWeight: 700, color: C.green }}>${size.price.toFixed(2)}</span>
                                </button>
                            ))}
                        </div>
                    )}
                    {shreddingSizeId && (
                        <div style={{ marginTop: 12 }}>
                            <label style={{ fontSize: 12, fontWeight: 600, color: C.textSub, display: 'block', marginBottom: 6 }}>How many bins of this size?</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <button type="button" onClick={() => setShreddingUnits(String(Math.max(1, (parseInt(shreddingUnits) || 1) - 1)))}
                                    style={{ width: 36, height: 36, borderRadius: 8, border: `1px solid ${C.border}`, background: '#fff', fontSize: 18, fontWeight: 700, color: C.text, cursor: 'pointer' }}>−</button>
                                <input type="number" min={1} value={shreddingUnits} onChange={e => setShreddingUnits(e.target.value)}
                                    style={{ width: 60, textAlign: 'center', padding: '8px 4px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 15, fontWeight: 700 }} />
                                <button type="button" onClick={() => setShreddingUnits(String((parseInt(shreddingUnits) || 1) + 1))}
                                    style={{ width: 36, height: 36, borderRadius: 8, border: `1px solid ${C.border}`, background: '#fff', fontSize: 18, fontWeight: 700, color: C.text, cursor: 'pointer' }}>+</button>
                                <span style={{ fontSize: 13, color: C.textSub }}>
                                    = ${((parseFloat(shreddingUnits) || 1) * (shreddingSizesForCustomerType.find(s => s.id === shreddingSizeId)?.price ?? 0)).toFixed(2)}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            )}
            {documentCategoriesRequired && !isBinDelivery && shreddingCustomerType && (
                <div style={{ marginBottom: 14, background: C.greenBg, borderRadius: 8, padding: 10, display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 13, color: C.textSub }}>Bin retrieval — flat fee, any size</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: C.green }}>${retrievalFee.toFixed(2)}</span>
                </div>
            )}
            <Input label="Recipient's name" value={recipientName} onChange={setRecipientName} />
            <Input label="Recipient's email" value={recipientEmail} onChange={setRecipientEmail} type="email" />
            <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 500, color: C.textSub, display: 'block', marginBottom: 6 }}>Personal message (optional)</label>
                <textarea value={message} onChange={e => setMessage(e.target.value)} rows={3}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }} />
            </div>
            <Btn onClick={startPurchase} disabled={loading}>{loading ? 'Loading…' : 'Continue to payment →'}</Btn>
        </Card>
    );
}

// ── Booking flow ──────────────────────────────────────────────────────────────

function BookingFlow({ onComplete, onDumpster }: { onComplete: () => void; onDumpster: () => void }) {
    const branding = useBranding();
    const svcNoun = branding.serviceNoun ?? null;
    const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
    const svcLabel = svcNoun ?? 'service';
    const svcLabelPlural = svcNoun ? svcNoun + 's' : 'services';
    const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
    const [serviceType, setServiceType] = useState('');
    const [address, setAddress] = useState('');
    const [pickupLat, setPickupLat] = useState(0);
    const [pickupLng, setPickupLng] = useState(0);
    const [pickupCity, setPickupCity] = useState<string | undefined>();
    const [pickupState, setPickupState] = useState<string | undefined>();
    const [pickupCounty, setPickupCounty] = useState<string | undefined>();
    const [pickupPostalCode, setPickupPostalCode] = useState<string | undefined>();
    const [pickupCountry, setPickupCountry] = useState<string | undefined>();
    const [coverage, setCoverage] = useState<'checking' | 'covered' | 'uncovered' | null>(null);
    const [units, setUnits] = useState('1');
    const [slot, setSlot] = useState('');
    const [tip, setTip] = useState(0);
    const [customTip, setCustomTip] = useState(false);
    const [customTipAmount, setCustomTipAmount] = useState('');
    const [quote, setQuote] = useState<QuoteResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [photoBase64, setPhotoBase64] = useState<string | null>(null);
    const [createdJobId, setCreatedJobId] = useState<string | null>(null);
    const [paymentClientSecret, setPaymentClientSecret] = useState('');
    // The authoritative amount for the payment form — CreateJobAsync computes its own real
    // Stripe Tax calculation independently of the earlier quote-time one (getQuote's preview), so
    // passing the client-computed `total` below to PaymentForm was displaying a guess instead of
    // the actual amount the backend just decided to authorize. Two separate Stripe Tax calls
    // should usually agree, but "usually" isn't good enough for a number promising what's about
    // to be authorized — use the real one the backend returns.
    const [authorizedAmount, setAuthorizedAmount] = useState<number | null>(null);
    const [serviceOptions, setServiceOptions] = useState<{ value: string; label: string; isFlatRate: boolean; allowPhotoUpload: boolean; requiresDonationCenter: boolean; requiresDocumentCategories: boolean }[]>([]);
    const [serviceTypesLoading, setServiceTypesLoading] = useState(true);
    const [donationCenters, setDonationCenters] = useState<DonationCenterDto[]>([]);
    const [donationCenterId, setDonationCenterId] = useState('');
    const [donationCenterDetails, setDonationCenterDetails] = useState<DonationCenterDto | null>(null);
    const [shreddingCategories, setShreddingCategories] = useState<string[]>([]);
    const [shreddingRequestType, setShreddingRequestType] = useState('');
    const [shreddingCustomerType, setShreddingCustomerType] = useState('');
    const [shreddingSizeId, setShreddingSizeId] = useState('');
    const [shreddingServiceLocation, setShreddingServiceLocation] = useState('');
    const [binStatus, setBinStatus] = useState<CustomerBinStatusDto | null>(null);
    const [binSizes, setBinSizes] = useState<ShreddingBinSizeDto[]>([]);
    const [giftCode, setGiftCode] = useState('');
    const [giftCodeChecking, setGiftCodeChecking] = useState(false);
    const [giftCodeValidAmount, setGiftCodeValidAmount] = useState<number | null>(null);
    const [giftCodeError, setGiftCodeError] = useState('');

    useEffect(() => {
        api.getServiceTypes().then(types => {
            const filtered = types.filter(t => t.value.toLowerCase() !== 'dumpster');
            setServiceOptions(filtered.map(t => ({ value: t.value, label: t.label, isFlatRate: t.isFlatRate, allowPhotoUpload: t.allowPhotoUpload, requiresDonationCenter: t.requiresDonationCenter, requiresDocumentCategories: t.requiresDocumentCategories })));
            if (filtered.length > 0) setServiceType(filtered[0].value);
        }).catch(() => {
            const fallback = [
                { value: 'Cardboard', label: 'Cardboard', isFlatRate: false, allowPhotoUpload: false, requiresDonationCenter: false, requiresDocumentCategories: false },
                { value: 'Furniture', label: 'Furniture', isFlatRate: false, allowPhotoUpload: false, requiresDonationCenter: false, requiresDocumentCategories: false },
                { value: 'YardDebris', label: 'Yard Debris', isFlatRate: false, allowPhotoUpload: false, requiresDonationCenter: false, requiresDocumentCategories: false },
                { value: 'General', label: 'General', isFlatRate: false, allowPhotoUpload: false, requiresDonationCenter: false, requiresDocumentCategories: false },
            ];
            setServiceOptions(fallback);
            setServiceType('Cardboard');
        }).finally(() => setServiceTypesLoading(false));
        api.getCustomerProfile().then(p => {
            if (p.defaultAddress) {
                setAddress(p.defaultAddress);
                // Always geocode fresh rather than trust stored defaultLat/defaultLng — some
                // accounts have coordinates that were saved independently of the address text
                // (a past backend gap, now closed) and no longer match what's displayed, which
                // silently broke the service-area check until the customer manually re-searched.
                geocodeAddress(p.defaultAddress).then(r => {
                    if (!r) return;
                    setPickupLat(r.lat);
                    setPickupLng(r.lng);
                    if (r.city) setPickupCity(r.city);
                    if (r.state) setPickupState(r.state);
                    if (r.county) setPickupCounty(r.county);
                    if (r.postalCode) setPickupPostalCode(r.postalCode);
                    if (r.country) setPickupCountry(r.country);
                });
            }
        }).catch(() => {});
    }, []);

    useEffect(() => {
        if (pickupLat === 0 || (!pickupState && !pickupPostalCode)) { setCoverage(null); return; }
        let cancelled = false;
        setCoverage('checking');
        api.checkServiceArea(pickupState, pickupCounty, pickupPostalCode, pickupLat, pickupLng)
            .then(r => { if (!cancelled) setCoverage(r.covered ? 'covered' : 'uncovered'); })
            .catch(() => { if (!cancelled) setCoverage(null); });
        return () => { cancelled = true; };
    }, [pickupLat, pickupLng, pickupState, pickupCounty, pickupPostalCode]);

    const tippingEnabled = useFeature('tipping');
    const dumpsterEnabled = useFeature('dumpsterRental');
    const flagsReady = useFeatureFlagsReady(); // see CustomerPortal's own useFeatureFlagsReady comment
    const serviceFeeEnabled = useFeature('serviceFee');
    const salesTaxEnabled = useFeature('salesTax');
    const tipOptions = [0, 15, 20, 25];
    const selectedOption = serviceOptions.find(o => o.value === serviceType);
    const isFlatRate = selectedOption?.isFlatRate ?? false;
    const photoUploadEnabled = selectedOption?.allowPhotoUpload ?? false;
    const donationCenterRequired = selectedOption?.requiresDonationCenter ?? false;
    const documentCategoriesRequired = selectedOption?.requiresDocumentCategories ?? false;

    // A validated gift code is only good for whatever service type it was checked against —
    // clear it if the customer changes their selection afterward so a stale "covered" state can't
    // ride along to a different (unvalidated) service type at submit time.
    useEffect(() => {
        setGiftCode(''); setGiftCodeValidAmount(null); setGiftCodeError('');
    }, [serviceType]);

    useEffect(() => {
        if (!donationCenterRequired) { setDonationCenterId(''); return; }
        api.getDonationCenters().then(setDonationCenters).catch(() => setDonationCenters([]));
    }, [donationCenterRequired]);

    useEffect(() => {
        if (!documentCategoriesRequired) {
            setShreddingCategories([]);
            setShreddingRequestType('');
            setShreddingCustomerType('');
            setShreddingSizeId('');
            setShreddingServiceLocation('');
            setBinStatus(null);
            setBinSizes([]);
            return;
        }
        api.getMyShreddingBinStatus().then(setBinStatus).catch(() => setBinStatus({ hasActiveBin: false, retrievalFee: 0 }));
        api.getShreddingBinSizes().then(setBinSizes).catch(() => setBinSizes([]));
    }, [documentCategoriesRequired]);

    // Only "deliver a new bin" makes sense before one's actually out there — reset off an option
    // that just became unavailable once we learn the customer has no active bin.
    useEffect(() => {
        if (binStatus?.hasActiveBin === false && shreddingRequestType !== 'BinDelivery') setShreddingRequestType('');
    }, [binStatus]);

    useEffect(() => {
        if (shreddingRequestType !== 'DestructionService') {
            setShreddingCategories([]);
            setShreddingServiceLocation('');
        }
        if (shreddingRequestType !== 'BinDelivery') { setShreddingSizeId(''); if (documentCategoriesRequired) setUnits('1'); }
    }, [shreddingRequestType]);

    const shreddingSizesForCustomerType = binSizes.filter(s =>
        shreddingCustomerType === 'Business' ? s.availableToBusiness : s.availableToResidential);

    const getQuote = async () => {
        if (coverage === 'uncovered') { setError("Sorry, we don't currently service this address."); return; }
        if (donationCenterRequired && !donationCenterId) { setError('Please select a donation center.'); return; }
        if (documentCategoriesRequired) {
            if (!shreddingCustomerType) { setError('Please select residential or business.'); return; }
            if (!shreddingRequestType) { setError('Please select what you need.'); return; }
            if (shreddingRequestType === 'BinDelivery' && !shreddingSizeId) { setError('Please select a bin size.'); return; }
            if (shreddingRequestType === 'DestructionService') {
                if (shreddingCategories.length === 0) { setError('Please select at least one document category.'); return; }
                if (!shreddingServiceLocation) { setError('Please select on-site or off-site destruction.'); return; }
            }
        }
        setError(''); setLoading(true);
        try {
            const q = await api.getQuote(serviceType, parseInt(units), { pickupCity, pickupState, pickupCounty, pickupPostalCode, pickupCountry },
                documentCategoriesRequired ? { shreddingRequestType, shreddingSizeId: shreddingSizeId || undefined, shreddingCustomerType } : undefined);
            setQuote(q);
            setStep(2);
        } catch (e: any) { setError(e.message); }
        finally { setLoading(false); }
    };

    const uploadPhoto = async (jobId: string, base64: string) => {
        try {
            const token = localStorage.getItem('br_token');
            await fetch(`${API_BASE}/api/photos/${jobId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ base64Image: base64 })
            });
        } catch (e) {
            console.warn('Photo upload failed — job still created', e);
        }
    };

    const checkGiftCode = async () => {
        if (!giftCode.trim()) return;
        setGiftCodeChecking(true); setGiftCodeError(''); setGiftCodeValidAmount(null);
        try {
            const r = await api.validateGiftVoucher(giftCode.trim(), serviceType,
                documentCategoriesRequired ? (shreddingSizeId || undefined) : undefined,
                documentCategoriesRequired ? shreddingRequestType : undefined,
                documentCategoriesRequired ? (parseInt(units) || 1) : undefined);
            if (r.valid) setGiftCodeValidAmount(r.amount ?? 0);
            else setGiftCodeError(r.error || "That code isn't valid.");
        } catch (e: any) { setGiftCodeError(e.message); }
        finally { setGiftCodeChecking(false); }
    };

    const confirmBooking = async () => {
        if (!quote) return;
        setError(''); setLoading(true);
        try {
            // The PaymentForm's "Back" button only sets step back to 2 — it doesn't cancel the
            // job that was already created for it. Clicking "Next — Payment" again used to call
            // api.createJob a second time, creating a duplicate order and orphaning the first
            // (still unpaid, PaymentStatus.Pending). If a job already exists from an earlier
            // attempt this session, reuse it — just fetch a fresh PaymentIntent — instead of
            // creating another one.
            let jobId = createdJobId;
            let subscriptionCovered = false;
            if (!jobId) {
                const job = await api.createJob({
                    serviceType,
                    pickupAddress: address,
                    pickupLat,
                    pickupLng,
                    estimatedUnits: parseInt(units),
                    scheduledTimeSlot: slot,
                    tipAmount: Math.round(tipAmount * 100) / 100,
                    pickupCity, pickupState, pickupCounty, pickupPostalCode, pickupCountry,
                    donationCenterId: donationCenterId || undefined,
                    shreddingDocumentCategories: shreddingCategories.length > 0 ? shreddingCategories : undefined,
                    shreddingRequestType: shreddingRequestType || undefined,
                    shreddingCustomerType: shreddingCustomerType || undefined,
                    shreddingSizeId: shreddingSizeId || undefined,
                    shreddingServiceLocation: shreddingServiceLocation || undefined,
                    giftVoucherCode: giftCodeValidAmount !== null ? giftCode.trim() : undefined,
                });
                jobId = job.id;
                // Gift-voucher-covered jobs get PaymentStatus.Authorized immediately too, same as
                // subscription-covered ones (both skip Stripe entirely) — checking paymentStatus
                // instead of just subscriptionCovered catches both without needing a second flag.
                subscriptionCovered = job.subscriptionCovered || job.paymentStatus === 'Authorized';
                setCreatedJobId(job.id);
                if (photoBase64) await uploadPhoto(job.id, photoBase64);
            }

            if (subscriptionCovered) {
                // Subscription-covered: pickup is already paid for. Every service reachable
                // through this flow (serviceOptions, from /api/jobs/service-types) always has a
                // real, active PricingRule — GetQuoteAsync throws otherwise — so quote.total here
                // is always a known, real price. The "price isn't known until a contractor
                // diagnoses it" ServiceCatalogPricing scenario belongs entirely to the separate
                // Estimate Builder / "Request an Estimate" flow, not this one; checking the
                // tenant-wide serviceCatalogEnabled flag here was skipping payment for every
                // booking on any tenant that also happens to use Estimate Builder, regardless of
                // whether this specific service type actually needed deferred pricing.
                setStep(4);
            } else {
                const intent = await api.createPaymentIntent(jobId);
                setPaymentClientSecret(intent.clientSecret);
                setAuthorizedAmount(intent.amount);
                setStep(3);
            }
        } catch (e: any) { setError(e.message); }
        finally { setLoading(false); }
    };

    const effectiveQuoteTotal = quote
        ? (quote.subscriberTotal !== null && quote.subscriberTotal !== undefined ? quote.subscriberTotal : quote.total)
        : 0;
    const tipAmount = !tippingEnabled || !quote ? 0
        : customTip ? (parseFloat(customTipAmount) || 0)
        : tip > 0 ? (effectiveQuoteTotal * tip / 100)
        : 0;
    const total = quote ? effectiveQuoteTotal + tipAmount + (quote.serviceFeeAmount || 0) + (quote.taxAmount || 0) : 0;

    return (
        <div style={{ maxWidth: 540, margin: '0 auto' }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
                {[1, 2, 3, 4].map(s => (
                    <div key={s} style={{ flex: 1, height: 4, borderRadius: 2, background: s <= step ? C.greenMid : C.border }} />
                ))}
            </div>
            <Alert message={error} />
            {step === 1 && (
                <Card>
                    <h2 style={{ margin: '0 0 20px', fontSize: 18 }}>{svcNoun === 'pickup' ? '📦' : '🛠️'} New {svcLabel} request</h2>
                    <div style={{ marginBottom: 16 }}>
                        <label style={{ fontSize: 13, fontWeight: 500, color: C.textSub, display: 'block', marginBottom: 8 }}>Service type</label>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 8 }}>
                            {serviceTypesLoading ? (
                                // Avoid a flash where the Dumpster tile (gated on a feature flag that may
                                // not have resolved to its real value yet — see flagsReady) appears alone
                                // before the fetched service types land — hold every tile behind the same
                                // loading state so they all appear together.
                                Array.from({ length: (flagsReady && dumpsterEnabled) ? 5 : 4 }).map((_, i) => (
                                    <div key={i} style={{ height: 82, borderRadius: 10, border: `2px solid ${C.border}`, background: C.bg, opacity: 0.6 }} />
                                ))
                            ) : (
                                <>
                                    {serviceOptions.map(opt => (
                                        <button key={opt.value} type="button" onClick={() => { setServiceType(opt.value); if (opt.isFlatRate) setUnits('1'); }}
                                            style={{ minHeight: 82, padding: '10px 8px', borderRadius: 10, border: `2px solid ${serviceType === opt.value ? C.greenMid : C.border}`, background: serviceType === opt.value ? C.greenBg : '#fff', cursor: 'pointer', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, transition: 'all 0.15s' }}>
                                            <div style={{ fontSize: 22 }}>
                                                {opt.value === 'Cardboard' ? '📦' : opt.value === 'Furniture' ? '🛋️' : opt.value === 'YardDebris' ? '🌿' : '🚚'}
                                            </div>
                                            {/* Service names are admin-configured per tenant (PricingRule.ServiceTypeName), not
                                                just the built-in short labels — wrap instead of truncating so a longer custom
                                                name is never cut off. */}
                                            <div style={{ fontSize: 12, fontWeight: serviceType === opt.value ? 700 : 400, color: serviceType === opt.value ? C.green : C.text, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.25 }}>
                                                {opt.label}
                                            </div>
                                        </button>
                                    ))}
                                    {flagsReady && dumpsterEnabled && (
                                        <button type="button" onClick={onDumpster}
                                            style={{ height: 82, padding: '0 8px', borderRadius: 10, border: `2px solid #6366F1`, background: '#EEF2FF', cursor: 'pointer', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                                            <div style={{ fontSize: 22 }}>🚛</div>
                                            <div style={{ fontSize: 12, fontWeight: 600, color: '#3730A3' }}>Dumpster</div>
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                    {photoUploadEnabled && (
                        <PhotoUpload
                            serviceType={serviceType}
                            onEstimate={({ estimatedUnits }, base64) => {
                                setUnits(estimatedUnits.toString());
                                setPhotoBase64(base64);
                            }}
                        />
                    )}
                    {donationCenterRequired && (
                        <div style={{ marginBottom: 16 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: C.textSub, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Donation center</div>
                            {donationCenters.length === 0 ? (
                                <div style={{ fontSize: 13, color: C.textSub }}>No donation centers are set up yet — contact support.</div>
                            ) : (
                                <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4, WebkitOverflowScrolling: 'touch' }}>
                                    {donationCenters.map(dc => (
                                        <div key={dc.id} style={{ position: 'relative', flexShrink: 0, width: 200, height: 76 }}>
                                            <button type="button" onClick={() => setDonationCenterId(dc.id)}
                                                style={{ textAlign: 'left', padding: '10px 14px', paddingRight: 32, width: '100%', height: '100%', borderRadius: 10, border: `2px solid ${donationCenterId === dc.id ? C.greenMid : C.border}`, background: donationCenterId === dc.id ? C.greenBg : '#fff', cursor: 'pointer', boxSizing: 'border-box', overflow: 'hidden' }}>
                                                <div style={{ fontWeight: 700, fontSize: 14, color: donationCenterId === dc.id ? C.green : C.text, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as any }}>{dc.name}</div>
                                                <div style={{ fontSize: 12, color: C.textSub, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dc.address}</div>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={e => { e.stopPropagation(); setDonationCenterDetails(dc); }}
                                                aria-label={`Details about ${dc.name}`}
                                                style={{
                                                    position: 'absolute', top: 8, right: 8,
                                                    width: 20, height: 20, borderRadius: '50%',
                                                    border: `1px solid ${C.border}`, background: '#fff',
                                                    color: C.textSub, fontSize: 11, fontWeight: 700,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    cursor: 'pointer', padding: 0,
                                                }}
                                            >
                                                i
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    {donationCenterDetails && (
                        <DonationCenterDetailsModal center={donationCenterDetails} onClose={() => setDonationCenterDetails(null)} />
                    )}
                    {documentCategoriesRequired && (
                        <div style={{ marginBottom: 16 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: C.textSub, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
                                Residential or business?
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                {[
                                    { value: 'Residential', label: 'Residential' },
                                    { value: 'Business', label: 'Business' },
                                ].map(opt => (
                                    <button key={opt.value} type="button"
                                        onClick={() => setShreddingCustomerType(opt.value)}
                                        style={{
                                            flex: 1, padding: '10px 14px', borderRadius: 10,
                                            border: `2px solid ${shreddingCustomerType === opt.value ? C.greenMid : C.border}`,
                                            background: shreddingCustomerType === opt.value ? C.greenBg : '#fff',
                                            cursor: 'pointer',
                                        }}>
                                        <div style={{ fontSize: 13.5, fontWeight: shreddingCustomerType === opt.value ? 700 : 600, color: shreddingCustomerType === opt.value ? C.green : C.text }}>{opt.label}</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    {documentCategoriesRequired && (
                        <div style={{ marginBottom: 16 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: C.textSub, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
                                What do you need?
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {[
                                    { value: 'BinDelivery', label: 'Deliver a new bin', desc: 'First time, or you want an additional bin.', disabled: false },
                                    { value: 'DestructionService', label: 'Service my existing bin', desc: 'Pick up, destroy, and return my bin empty.', disabled: binStatus?.hasActiveBin === false },
                                    { value: 'BinRetrieval', label: 'Pick up / cancel my bin', desc: "I'm done — pick up the bin for good.", disabled: binStatus?.hasActiveBin === false },
                                ].map(opt => (
                                    <button key={opt.value} type="button" disabled={opt.disabled}
                                        onClick={() => setShreddingRequestType(opt.value)}
                                        style={{
                                            textAlign: 'left', padding: '10px 14px', borderRadius: 10,
                                            border: `2px solid ${shreddingRequestType === opt.value ? C.greenMid : C.border}`,
                                            background: shreddingRequestType === opt.value ? C.greenBg : opt.disabled ? C.bg : '#fff',
                                            cursor: opt.disabled ? 'not-allowed' : 'pointer',
                                            opacity: opt.disabled ? 0.55 : 1,
                                        }}>
                                        <div style={{ fontSize: 13.5, fontWeight: shreddingRequestType === opt.value ? 700 : 600, color: shreddingRequestType === opt.value ? C.green : C.text }}>{opt.label}</div>
                                        <div style={{ fontSize: 12, color: C.textSub, marginTop: 2 }}>
                                            {opt.disabled ? "You don't currently have a bin on file." : opt.desc}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    {documentCategoriesRequired && shreddingRequestType === 'BinDelivery' && (
                        <div style={{ marginBottom: 16 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: C.textSub, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
                                Bin size
                            </div>
                            {!shreddingCustomerType ? (
                                <div style={{ fontSize: 13, color: C.textMuted }}>Select residential or business above first.</div>
                            ) : shreddingSizesForCustomerType.length === 0 ? (
                                <div style={{ fontSize: 13, color: C.textSub }}>No bin sizes are set up yet — contact support.</div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {shreddingSizesForCustomerType.map(size => (
                                        <button key={size.id} type="button" onClick={() => setShreddingSizeId(size.id)}
                                            style={{
                                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                textAlign: 'left', padding: '10px 14px', borderRadius: 10,
                                                border: `2px solid ${shreddingSizeId === size.id ? C.greenMid : C.border}`,
                                                background: shreddingSizeId === size.id ? C.greenBg : '#fff',
                                                cursor: 'pointer',
                                            }}>
                                            <span style={{ fontSize: 13.5, fontWeight: shreddingSizeId === size.id ? 700 : 600, color: shreddingSizeId === size.id ? C.green : C.text }}>{size.name}</span>
                                            <span style={{ fontSize: 14, fontWeight: 700, color: C.green }}>${size.price.toFixed(2)}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                            {shreddingSizeId && (
                                <div style={{ marginTop: 12 }}>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: C.textSub, display: 'block', marginBottom: 6 }}>How many bins of this size?</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <button type="button" onClick={() => setUnits(String(Math.max(1, (parseInt(units) || 1) - 1)))}
                                            style={{ width: 36, height: 36, borderRadius: 8, border: `1px solid ${C.border}`, background: '#fff', fontSize: 18, fontWeight: 700, color: C.text, cursor: 'pointer' }}>−</button>
                                        <input type="number" min={1} value={units} onChange={e => setUnits(e.target.value)}
                                            style={{ width: 60, textAlign: 'center', padding: '8px 4px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 15, fontWeight: 700 }} />
                                        <button type="button" onClick={() => setUnits(String((parseInt(units) || 1) + 1))}
                                            style={{ width: 36, height: 36, borderRadius: 8, border: `1px solid ${C.border}`, background: '#fff', fontSize: 18, fontWeight: 700, color: C.text, cursor: 'pointer' }}>+</button>
                                        <span style={{ fontSize: 13, color: C.textSub }}>
                                            = ${((parseFloat(units) || 1) * (shreddingSizesForCustomerType.find(s => s.id === shreddingSizeId)?.price ?? 0)).toFixed(2)}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {documentCategoriesRequired && shreddingRequestType === 'DestructionService' && (
                        <div style={{ marginBottom: 16 }}>
                            {binStatus?.activeBinSizeName && (
                                <div style={{ background: C.greenBg, borderRadius: 8, padding: 10, marginBottom: 12, fontSize: 12, color: C.textSub }}>
                                    Your bin: <strong>{binStatus.activeBinSizeName}</strong> — <strong>${(binStatus.activeBinPrice ?? 0).toFixed(2)}</strong>
                                </div>
                            )}
                            <div style={{ fontSize: 12, fontWeight: 600, color: C.textSub, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
                                Where should destruction happen?
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                                {[
                                    { value: 'OnSite', label: 'On-site', desc: 'Shredded right at your location, witnessed.' },
                                    { value: 'OffSite', label: 'Off-site', desc: 'Picked up and shredded at our secure facility.' },
                                ].map(opt => (
                                    <button key={opt.value} type="button" onClick={() => setShreddingServiceLocation(opt.value)}
                                        style={{
                                            textAlign: 'left', padding: '10px 14px', borderRadius: 10,
                                            border: `2px solid ${shreddingServiceLocation === opt.value ? C.greenMid : C.border}`,
                                            background: shreddingServiceLocation === opt.value ? C.greenBg : '#fff',
                                            cursor: 'pointer',
                                        }}>
                                        <div style={{ fontSize: 13.5, fontWeight: shreddingServiceLocation === opt.value ? 700 : 600, color: shreddingServiceLocation === opt.value ? C.green : C.text }}>{opt.label}</div>
                                        <div style={{ fontSize: 12, color: C.textSub, marginTop: 2 }}>{opt.desc}</div>
                                    </button>
                                ))}
                            </div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: C.textSub, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
                                What's being destroyed? (select all that apply)
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {[
                                    { value: 'HrRecords', label: 'HR records' },
                                    { value: 'FinancialRecords', label: 'Financial records' },
                                    { value: 'Phi', label: 'PHI / medical' },
                                    { value: 'LegalDocuments', label: 'Legal documents' },
                                    { value: 'TaxRecords', label: 'Tax records' },
                                    { value: 'CustomerPii', label: 'Customer PII' },
                                    { value: 'Other', label: 'Other' },
                                ].map(cat => {
                                    const selected = shreddingCategories.includes(cat.value);
                                    return (
                                        <button key={cat.value} type="button"
                                            onClick={() => setShreddingCategories(prev => selected ? prev.filter(c => c !== cat.value) : [...prev, cat.value])}
                                            style={{
                                                padding: '8px 14px', borderRadius: 20, cursor: 'pointer',
                                                border: `1.5px solid ${selected ? C.greenMid : C.border}`,
                                                background: selected ? C.greenBg : '#fff',
                                                color: selected ? C.green : C.text,
                                                fontSize: 13, fontWeight: 600,
                                            }}>
                                            {cat.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {documentCategoriesRequired && shreddingRequestType === 'BinRetrieval' && (
                        <div style={{ background: C.greenBg, borderRadius: 8, padding: 10, marginBottom: 16, fontSize: 12, color: C.textSub }}>
                            Bin retrieval fee: <strong>${(binStatus?.retrievalFee ?? 0).toFixed(2)}</strong>
                        </div>
                    )}
                    <AddressAutocomplete
                        label={`${cap(svcLabel)} address`}
                        value={address}
                        onChange={({ address, lat, lng, city, state, county, postalCode, country }) => {
                            setAddress(address);
                            setPickupLat(lat);
                            setPickupLng(lng);
                            setPickupCity(city);
                            setPickupState(state);
                            setPickupCounty(county);
                            setPickupPostalCode(postalCode);
                            setPickupCountry(country);
                        }}
                        placeholder={`Enter ${svcLabel} address`}
                        required
                    />
                    {coverage === 'checking' && (
                        <div style={{ fontSize: 12, color: C.textMuted, marginTop: -8, marginBottom: 14 }}>Checking service area…</div>
                    )}
                    {coverage === 'uncovered' && (
                        <div style={{ fontSize: 12, color: '#DC2626', marginTop: -8, marginBottom: 14 }}>Sorry, we don't currently service this address.</div>
                    )}
                    {/* Shredding owns its own quantity control (bin count, inside the size picker
                        below) instead of this generic field — isFlatRate here depends on whatever
                        the PricingRule's PricePerUnit happens to be, unrelated to whether a
                        quantity actually applies to this booking. */}
                    {!isFlatRate && !documentCategoriesRequired && (
                        <Input
                            label={photoUploadEnabled ? 'Estimated units — auto-filled from photo' : 'Estimated units'}
                            value={units}
                            onChange={setUnits}
                            type="number"
                        />
                    )}
                    <DateTimePicker label={`Preferred ${svcLabel} time`} value={slot} onChange={setSlot} required />
                    <Btn
                        onClick={getQuote}
                        disabled={loading || !serviceType || !address.trim() || pickupLat === 0 || !slot || coverage === 'uncovered' || coverage === 'checking'}
                        fullWidth size="lg" type="button"
                    >
                        {loading ? 'Getting quote...' : 'Get instant quote ->'}
                    </Btn>
                </Card>
            )}
            {step === 2 && quote && (
                <Card>
                    <h2 style={{ margin: '0 0 20px', fontSize: 18 }}>💰 Review your booking</h2>
                    <div style={{ background: C.greenBg, borderRadius: 10, padding: 20, marginBottom: 20 }}>
                        {!isFlatRate && (
                            <>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                    <span style={{ color: C.textSub, fontSize: 13 }}>Base price</span>
                                    <span style={{ fontWeight: 600 }}>${quote.basePrice.toFixed(2)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                    <span style={{ color: C.textSub, fontSize: 13 }}>{units} {quote.unitLabel}s @ ${quote.pricePerUnit.toFixed(2)}</span>
                                    <span style={{ fontWeight: 600 }}>${(quote.total - quote.basePrice).toFixed(2)}</span>
                                </div>
                            </>
                        )}
                        {isFlatRate && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                <span style={{ color: C.textSub, fontSize: 13 }}>Flat rate {svcLabel}</span>
                                <span style={{ fontWeight: 600 }}>${quote.total.toFixed(2)}</span>
                            </div>
                        )}
                        {quote.subscriberTotal !== null && quote.subscriberTotal !== undefined && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, padding: '6px 10px', background: C.greenPale, borderRadius: 6 }}>
                                <span style={{ color: C.green, fontSize: 13, fontWeight: 600 }}>
                                    ★ Member price
                                </span>
                                <span style={{ fontWeight: 700, color: C.green }}>
                                    {quote.subscriberTotal === 0 ? 'FREE (covered)' : `$${quote.subscriberTotal.toFixed(2)}`}
                                </span>
                            </div>
                        )}
                        {tippingEnabled && tip > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                <span style={{ color: C.textSub, fontSize: 13 }}>Tip ({tip}%)</span>
                                <span style={{ fontWeight: 600 }}>${tipAmount.toFixed(2)}</span>
                            </div>
                        )}
                        {serviceFeeEnabled && quote.serviceFeeAmount > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                <span style={{ color: C.textSub, fontSize: 13 }}>{quote.serviceFeeLabel}</span>
                                <span style={{ fontWeight: 600 }}>${quote.serviceFeeAmount.toFixed(2)}</span>
                            </div>
                        )}
                        {salesTaxEnabled && quote.taxAmount > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                <span style={{ color: C.textSub, fontSize: 13 }}>Tax</span>
                                <span style={{ fontWeight: 600 }}>${quote.taxAmount.toFixed(2)}</span>
                            </div>
                        )}
                        {photoBase64 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                <span style={{ color: C.textSub, fontSize: 13 }}>📷 Photo attached</span>
                                <span style={{ color: C.greenMid, fontSize: 12 }}>✓ AI estimated</span>
                            </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 12, borderTop: `1px solid ${C.greenPale}` }}>
                            <span style={{ fontWeight: 700, fontSize: 16 }}>Total</span>
                            <span style={{ fontWeight: 700, fontSize: 20, color: C.green }}>${total.toFixed(2)}</span>
                        </div>
                    </div>
                    <div style={{ marginBottom: 20 }}>
                        <label style={{ fontSize: 12, fontWeight: 500, color: C.textSub, display: 'block', marginBottom: 8 }}>Have a gift voucher code?</label>
                        {giftCodeValidAmount !== null ? (
                            <div style={{ background: C.greenBg, borderRadius: 8, padding: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: 13, color: C.green, fontWeight: 600 }}>✓ Covered by gift voucher — $0.00 due</span>
                                <button onClick={() => { setGiftCode(''); setGiftCodeValidAmount(null); }} style={{ background: 'none', border: 'none', color: C.textSub, fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>Remove</button>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', gap: 8 }}>
                                <input
                                    value={giftCode}
                                    onChange={e => { setGiftCode(e.target.value); setGiftCodeError(''); }}
                                    placeholder="e.g. GIFT-K7M2QX"
                                    style={{ flex: 1, padding: '8px 10px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 14, boxSizing: 'border-box' as const }}
                                />
                                <Btn variant="outline" onClick={checkGiftCode} disabled={giftCodeChecking || !giftCode.trim()}>
                                    {giftCodeChecking ? 'Checking…' : 'Apply'}
                                </Btn>
                            </div>
                        )}
                        {giftCodeError && <div style={{ fontSize: 12, color: '#C0392B', marginTop: 6 }}>{giftCodeError}</div>}
                    </div>
                    {tippingEnabled && giftCodeValidAmount === null && (
                        <div style={{ marginBottom: 20 }}>
                            <label style={{ fontSize: 12, fontWeight: 500, color: C.textSub, display: 'block', marginBottom: 10 }}>Add a tip</label>
                            <div style={{ display: 'flex', gap: 8 }}>
                                {tipOptions.map(t => (
                                    <button key={t} onClick={() => { setTip(t); setCustomTip(false); }} style={{ flex: 1, padding: '8px 4px', borderRadius: 8, border: `2px solid ${!customTip && tip === t ? C.greenMid : C.border}`, background: !customTip && tip === t ? C.greenBg : '#fff', color: !customTip && tip === t ? C.green : C.text, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                                        {t === 0 ? 'None' : `${t}%`}
                                    </button>
                                ))}
                                <button onClick={() => setCustomTip(true)} style={{ flex: 1, padding: '8px 4px', borderRadius: 8, border: `2px solid ${customTip ? C.greenMid : C.border}`, background: customTip ? C.greenBg : '#fff', color: customTip ? C.green : C.text, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                                    Custom
                                </button>
                            </div>
                            {customTip && (
                                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ fontSize: 15, color: C.textSub }}>$</span>
                                    <input
                                        type="number" min="0" step="0.01" autoFocus
                                        value={customTipAmount}
                                        onChange={e => setCustomTipAmount(e.target.value)}
                                        placeholder="0.00"
                                        style={{ flex: 1, padding: '8px 10px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 14, boxSizing: 'border-box' as const }}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    <div style={{ display: 'flex', gap: 10 }}>
                        <Btn variant="outline" onClick={() => setStep(1)}>Back</Btn>
                        <Btn onClick={confirmBooking} disabled={loading} fullWidth size="lg">
                            {loading ? 'Creating booking...' : 'Next — Payment →'}
                        </Btn>
                    </div>
                </Card>
            )}
            {step === 3 && paymentClientSecret && createdJobId && (
                <PaymentForm
                    jobId={createdJobId}
                    amount={authorizedAmount ?? total}
                    clientSecret={paymentClientSecret}
                    onSuccess={async () => {
                        await confirmAuthWithRetry(() => api.confirmJobAuthorization(createdJobId!), "job");
                        setStep(4);
                    }}
                    onCancel={() => setStep(2)}
                />
            )}
            {step === 4 && (
                <Card style={{ textAlign: 'center', padding: 48 }}>
                    <div style={{ fontSize: 52 }}>✅</div>
                    <h2 style={{ marginTop: 16, color: C.green }}>Booking confirmed!</h2>
                    {createdJobId && !paymentClientSecret ? (
                        <p style={{ color: C.textSub }}>
                            This {svcLabel} is covered by your subscription. A nearby contractor will accept your job shortly.
                        </p>
                    ) : (
                        <p style={{ color: C.textSub }}>
                            We've placed a temporary authorization hold on your card — this is not a charge, and
                            your bank may show it as pending. You're only charged once the {svcLabel} is complete;
                            if it's cancelled, the hold is released.
                        </p>
                    )}
                    {photoBase64 && <p style={{ color: C.greenMid, fontSize: 13 }}>📷 Your photo has been saved with this job.</p>}
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 24 }}>
                        <Btn onClick={onComplete} size="lg">View my {svcLabelPlural}</Btn>
                    </div>
                </Card>
            )}
        </div>
    );
}

// ── Live tracking view ────────────────────────────────────────────────────────

function TrackingView({ job, location, connected, connectionError, onBack, onRefresh }: {
    job: Job;
    location: any;
    connected: boolean;
    connectionError?: string | null;
    onBack: () => void;
    onRefresh: () => void;
}) {
    const estimatesEnabled = useFeature('estimatesAndQuotes');
    const branding = useBranding();
    const svcLabel = branding.serviceNoun ?? 'service';
    const contractorLabelLower = (branding.contractorNoun || 'Contractor').toLowerCase();
    const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
    const [lastEta, setLastEta] = useState<number | null>(null);
    const [lastDistance, setLastDistance] = useState<number | null>(null);
    const [adjustment, setAdjustment] = useState<any>(null);
    const [showAdjustment, setShowAdjustment] = useState(false);
    const [pendingEstimate, setPendingEstimate] = useState<EstimateDto | null>(null);
    const [estimateLineItems, setEstimateLineItems] = useState<JobLineItemDto[]>([]);

    // TREK-78: poll for a pending estimate while the job is in progress — the contractor
    // sends one mid-visit (after diagnosing), so there's no SignalR event to key off yet.
    useEffect(() => {
        if (!estimatesEnabled || job.status !== 'InProgress') { setPendingEstimate(null); return; }
        let cancelled = false;
        const check = () => {
            api.getEstimate(job.id).then(est => {
                if (cancelled) return;
                if (est?.status === 'Pending') {
                    setPendingEstimate(est);
                    api.getJobLineItems(job.id).then(summary => { if (!cancelled) setEstimateLineItems(summary.lineItems); }).catch(() => {});
                } else {
                    setPendingEstimate(null);
                }
            });
        };
        check();
        const interval = setInterval(check, 8000);
        return () => { cancelled = true; clearInterval(interval); };
    }, [estimatesEnabled, job.status, job.id]);

    useEffect(() => {
        if (location?.etaMinutes != null) setLastEta(location.etaMinutes);
        if (location?.distanceMiles != null) setLastDistance(location.distanceMiles);

        // Show price adjustment modal when SignalR pushes PriceAdjustmentPending
        if (location?.jobStatus === 'PriceAdjustmentPending') {
            const token = localStorage.getItem('br_token');
            fetch(`${API_BASE}/api/price-adjustment/${job.id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            })
                .then(r => r.ok ? r.json() : null)
                .then(data => {
                    if (data) { setAdjustment(data); setShowAdjustment(true); }
                })
                .catch(() => {});
        }
    }, [location, job.id]);

    const statusSteps = ['Accepted', 'EnRoute', 'Arrived', 'InProgress', 'Completed'];
    const currentStatus = location?.jobStatus === 'PriceAdjustmentPending'
        ? 'InProgress'
        : location?.jobStatus || job.status;
    const currentIdx = statusSteps.indexOf(currentStatus);

    const statusLabels: Record<string, string> = {
        Pending: `Waiting for ${contractorLabelLower}`,
        Accepted: 'Contractor accepted',
        EnRoute: 'Contractor en route',
        Arrived: 'Contractor arrived!',
        InProgress: `${cap(svcLabel)} in progress`,
        Completed: `${cap(svcLabel)} complete ✓`,
    };

    return (
        <>
            {showAdjustment && adjustment && (
                <PriceAdjustmentModal
                    jobId={job.id}
                    estimatedUnits={adjustment.estimatedUnits}
                    actualUnits={adjustment.actualUnits}
                    originalPrice={adjustment.originalPrice}
                    adjustedPrice={adjustment.adjustedPrice}
                    serviceFeeAmount={adjustment.serviceFeeAmount}
                    taxAmount={adjustment.taxAmount}
                    contractorNotes={adjustment.notes}
                    onApprove={() => { setShowAdjustment(false); onRefresh(); onBack(); }}
                    onDispute={() => { setShowAdjustment(false); onRefresh(); }}
                />
            )}
            {pendingEstimate && (
                <EstimateApprovalModal
                    jobId={job.id}
                    estimate={pendingEstimate}
                    lineItems={estimateLineItems}
                    onResolved={() => { setPendingEstimate(null); onRefresh(); }}
                />
            )}
            <div style={{ maxWidth: 600, margin: '0 auto' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                    <Btn variant="ghost" onClick={() => { onRefresh(); onBack(); }}>← Back</Btn>
                    <h2 style={{ margin: 0, fontSize: 18 }}>Live tracking</h2>
                    <Badge label={currentStatus} />
                    {job.priceAdjustmentStatus === 'Pending' && (
                        <Btn size="sm" onClick={() => setShowAdjustment(true)}>💰 Review charge</Btn>
                    )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <TrackerCard
                        jobId={job.id}
                        contractor={job.contractor}
                        etaMinutes={lastEta}
                        distanceMiles={lastDistance}
                        connected={connected}
                        connectionError={connectionError}
                    />
                    <Card>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.textSub, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 16 }}>Status</div>
                        {statusSteps.map((s, i) => {
                            const done = i < currentIdx;
                            const active = i === currentIdx;
                            return (
                                <div key={s} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', paddingBottom: i < statusSteps.length - 1 ? 16 : 0 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 16 }}>
                                        <div style={{ width: 12, height: 12, borderRadius: '50%', background: done ? C.greenMid : active ? C.green : C.border, border: active ? `3px solid ${C.greenPale}` : 'none', marginTop: 2 }} />
                                        {i < statusSteps.length - 1 && <div style={{ width: 2, height: 24, background: done ? C.greenMid : C.border, margin: '3px 0' }} />}
                                    </div>
                                    <div style={{ fontSize: 13, color: active ? C.green : done ? C.text : C.textMuted, fontWeight: active ? 600 : 400, paddingBottom: 4 }}>
                                        {statusLabels[s] || s}
                                    </div>
                                </div>
                            );
                        })}
                    </Card>
                    <Card>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.textSub, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Job details</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <div><div style={{ fontSize: 11, color: C.textMuted }}>Address</div><div style={{ fontWeight: 500, fontSize: 13 }}>{job.pickupAddress}</div></div>
                            <div><div style={{ fontSize: 11, color: C.textSub }}>Service</div><div style={{ fontWeight: 500, fontSize: 13 }}>{job.serviceType}</div></div>
                            <div><div style={{ fontSize: 11, color: C.textMuted }}>Units</div><div style={{ fontWeight: 500, fontSize: 13 }}>{job.estimatedUnits}</div></div>
                            <div><div style={{ fontSize: 11, color: C.textMuted }}>Total</div><div style={{ fontWeight: 500, fontSize: 13, color: C.green }}>${(job.grandTotal ?? job.totalPrice).toFixed(2)}</div></div>
                        </div>
                    </Card>
                </div>
            </div>
        </>
    );
}

// ── Rating modal ──────────────────────────────────────────────────────────────

function RatingModal({ job, onClose }: { job: Job; onClose: () => void }) {
    const [stars, setStars] = useState(5);
    const [comment, setComment] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const submit = async () => {
        setError(''); setLoading(true);
        try { await api.submitRating(job.id, stars, comment); onClose(); }
        catch (e: any) { setError(e.message); }
        finally { setLoading(false); }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}>
            <Card style={{ width: '100%', maxWidth: 400 }}>
                <h3 style={{ margin: '0 0 6px' }}>Rate your pickup</h3>
                {job.contractor && <p style={{ color: C.textSub, fontSize: 13, margin: '0 0 20px' }}>How did {job.contractor.name} do?</p>}
                <Alert message={error} />
                <div style={{ marginBottom: 16 }}><StarRating value={stars} onChange={setStars} /></div>
                <div style={{ marginBottom: 20 }}>
                    <label style={{ fontSize: 12, fontWeight: 500, color: C.textSub, display: 'block', marginBottom: 5 }}>Comment (optional)</label>
                    <textarea value={comment} onChange={e => setComment(e.target.value)} rows={3} style={{ width: '100%', padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 14, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} placeholder="Great service, very fast..." />
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                    <Btn variant="outline" onClick={onClose}>Cancel</Btn>
                    <Btn onClick={submit} disabled={loading} fullWidth>{loading ? 'Submitting...' : 'Submit rating'}</Btn>
                </div>
            </Card>
        </div>
    );
}

// ── Job Chat ──────────────────────────────────────────────────────────────────

function JobChat({ jobId, onClose }: { jobId: string; onClose: () => void }) {
    const branding = useBranding();
    const contractorLabelLower = (branding.contractorNoun || 'Contractor').toLowerCase();
    const { user } = useAuth();
    const [messages, setMessages] = useState<MessageItem[]>([]);
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);
    const bottomRef = React.useRef<HTMLDivElement>(null);
    const hubRef = React.useRef<signalR.HubConnection | null>(null);

    useEffect(() => {
        api.getMessages(jobId).then(msgs => { setMessages(msgs); setLoading(false); }).catch(() => setLoading(false));
        const token = localStorage.getItem('br_token');
        const hub = new signalR.HubConnectionBuilder()
            .withUrl(`${API_BASE}/hubs/tracking?access_token=${token}`, { withCredentials: false })
            .withAutomaticReconnect().build();
        hub.on('NewMessage', (msg: MessageItem) => {
            if (msg.jobId === jobId) setMessages(prev => prev.some(m => m.id === msg.id) ? prev : [...prev, msg]);
        });
        hub.start().then(() => hub.invoke('SubscribeToJob', jobId).catch(() => {})).catch(console.error);
        hubRef.current = hub;
        return () => { hub.stop(); };
    }, [jobId]);

    useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

    const send = async () => {
        if (!text.trim() || sending) return;
        setSending(true);
        try {
            await api.sendMessage(jobId, text.trim());
            setText('');
        } catch (e: any) { alert(e.message); }
        finally { setSending(false); }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ width: '100%', maxWidth: 500, background: '#fff', borderRadius: '16px 16px 0 0', display: 'flex', flexDirection: 'column', maxHeight: '70vh' }}>
                <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontWeight: 700, fontSize: 16 }}>💬 Chat with {contractorLabelLower}</div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: C.textMuted }}>✕</button>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {loading ? <Spinner /> : messages.length === 0
                        ? <div style={{ textAlign: 'center', color: C.textMuted, padding: 32 }}>No messages yet. Start the conversation!</div>
                        : messages.map((m: MessageItem) => {
                            const isMe = m.senderId === user?.userId;
                            return (
                                <div key={m.id} style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                                    <div style={{ maxWidth: '75%', padding: '10px 14px', borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px', background: isMe ? C.greenMid : C.bg, color: isMe ? '#fff' : C.text }}>
                                        {!isMe && <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4, color: C.textSub }}>{m.senderRole}</div>}
                                        <div style={{ fontSize: 14 }}>{m.text}</div>
                                        <div style={{ fontSize: 10, marginTop: 4, opacity: 0.7, textAlign: 'right' }}>
                                            {new Date(m.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    }
                    <div ref={bottomRef} />
                </div>
                <div style={{ padding: '12px 16px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: 10 }}>
                    <input value={text} onChange={e => setText(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
                        placeholder="Type a message..."
                        style={{ flex: 1, padding: '10px 14px', border: `1px solid ${C.border}`, borderRadius: 24, fontSize: 14, outline: 'none' }} />
                    <Btn onClick={send} disabled={!text.trim() || sending} size="sm">{sending ? '…' : 'Send'}</Btn>
                </div>
            </div>
        </div>
    );
}

// ── Dumpster Rental View ──────────────────────────────────────────────────────

function DumpsterView({ onComplete }: { onComplete?: () => void }) {
    const loadingJobsEnabled = useFeature('loadingJobs');
    const [step, setStep] = useState<'browse' | 'configure' | 'confirm' | 'payment' | 'orders'>('browse');
    const [pendingPayment, setPendingPayment] = useState<{ orderId: string; clientSecret: string; amount: number } | null>(null);
    const [vendors, setVendors] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [orders, setOrders] = useState<any[]>([]);
    const hubRef = useRef<signalR.HubConnection | null>(null);
    const subscribedOrdersRef = useRef<Set<string>>(new Set());
    const [runnerLocations, setRunnerLocations] = useState<Record<string, any>>({});
    const [trackingLoadingJobId, setTrackingLoadingJobId] = useState<string | null>(null);
    const ordersRef = useRef<any[]>([]);

    // Booking state
    const [selectedVendor, setSelectedVendor] = useState<any>(null);
    const [selectedSize, setSelectedSize] = useState<any>(null);
    const [address, setAddress] = useState('');
    const [deliveryLat, setDeliveryLat] = useState(0);
    const [deliveryLng, setDeliveryLng] = useState(0);
    const [deliveryCity, setDeliveryCity] = useState<string | undefined>();
    const [deliveryState, setDeliveryState] = useState<string | undefined>();
    const [deliveryPostalCode, setDeliveryPostalCode] = useState<string | undefined>();
    const [deliveryCountry, setDeliveryCountry] = useState<string | undefined>();
    const [deliveryDate, setDeliveryDate] = useState('');
    const [rentalDays, setRentalDays] = useState(7);
    const [loadingRequested, setLoadingRequested] = useState(false);
    const [runnersRequested, setRunnersRequested] = useState(1);
    const [quote, setQuote] = useState<any>(null);
    const [quoteLoading, setQuoteLoading] = useState(false);
    const [booking, setBooking] = useState(false);
    const [editingOrder, setEditingOrder] = useState<any>(null);
    const [editForm, setEditForm] = useState<any>({});

    const subscribeToOrders = useCallback((orderList: any[]) => {
        const hub = hubRef.current;
        if (!hub || hub.state !== signalR.HubConnectionState.Connected) return;
        orderList.forEach(o => {
            if (!subscribedOrdersRef.current.has(o.id)) {
                hub.invoke('SubscribeToDumpsterOrder', o.id)
                    .then(() => subscribedOrdersRef.current.add(o.id))
                    .catch(() => {});
            }
        });
    }, []);

    useEffect(() => {
        loadVendors();
        loadOrders();

        const token = localStorage.getItem('br_token');
        const hub = new signalR.HubConnectionBuilder()
            .withUrl(`${API_BASE}/hubs/tracking?access_token=${token}`, { withCredentials: false })
            .withAutomaticReconnect()
            .build();

        hub.on('DumpsterOrderUpdated', () => { loadOrders(); });
        hub.on('DumpsterRunnerLocationUpdated', (data: any) => {
            setRunnerLocations(prev => ({ ...prev, [data.loadingJobId]: data }));
        });

        hub.start()
            .then(() => {
                // Subscribe to any orders that loaded while hub was connecting
                subscribeToOrders(ordersRef.current);
            })
            .catch(console.error);

        // Re-subscribe after reconnect — SignalR group memberships don't survive reconnect
        hub.onreconnected(() => {
            subscribedOrdersRef.current.clear();
            subscribeToOrders(ordersRef.current);
        });

        hubRef.current = hub;
        return () => { hub.stop(); };
    }, []);

    // Keep ordersRef current and subscribe any new orders once hub is connected
    useEffect(() => {
        ordersRef.current = orders;
        subscribeToOrders(orders);
    }, [orders, subscribeToOrders]);

    const loadVendors = async () => {
        setLoading(true);
        try {
            // Was hardcoded to a fixed Huntsville, AL coordinate for every customer — the
            // backend hard-filters vendors by distance from these coordinates, so anyone
            // elsewhere either saw no vendors or one that couldn't actually reach them. Uses the
            // browser's location (delivery address isn't picked until a later step).
            const coords = await new Promise<{ lat: number; lng: number } | null>(resolve => {
                if (!navigator.geolocation) { resolve(null); return; }
                navigator.geolocation.getCurrentPosition(
                    pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
                    () => resolve(null),
                    { timeout: 8000 }
                );
            });
            if (!coords) {
                setError('Enable location access to see dumpster vendors near you.');
                setVendors([]);
                return;
            }
            const data = await api.getDumpsterVendors(coords.lat, coords.lng);
            setVendors(data);
        } catch (e: any) { setError(e.message); }
        finally { setLoading(false); }
    };

    const loadOrders = async () => {
        try {
            const data = await api.getMyDumpsterOrders();
            setOrders(data);
        } catch { }
    };

    const getQuote = async (sizeId: string) => {
        setQuoteLoading(true);
        try {
            const q = await api.getDumpsterQuote(sizeId, rentalDays);
            setQuote(q);
        } catch (e: any) { setError(e.message); }
        finally { setQuoteLoading(false); }
    };

    const selectSize = (vendor: any, size: any) => {
        setSelectedVendor(vendor);
        setSelectedSize(size);
        setStep('configure');
        getQuote(size.id);
    };

    const confirmBooking = async () => {
        // Was falling back to a hardcoded Huntsville, AL coordinate whenever deliveryLat/Lng
        // were still 0 (e.g. a Places autocomplete glitch, or the field never actually
        // resolving a place) — silently booking the order against the wrong location instead of
        // catching the problem before submit.
        if (!address || !deliveryLat || !deliveryLng || !deliveryDate) { setError('Please fill all required fields.'); return; }
        setBooking(true); setError('');
        try {
            // Step 1: Create the order
            const order = await api.bookDumpster({
                sizeId: selectedSize.id,
                deliveryAddress: address,
                deliveryLat, deliveryLng,
                deliveryCity, deliveryState, deliveryPostalCode, deliveryCountry,
                requestedDeliveryDate: new Date(deliveryDate).toISOString(),
                rentalDays,
                loadingRequested: loadingJobsEnabled && loadingRequested,
                loadingRunnersRequested: (loadingJobsEnabled && loadingRequested) ? runnersRequested : 0,
            });

            // Step 2: Get payment intent
            const intent = await api.getDumpsterPaymentIntent(order.id);

            // Step 3: Show Stripe payment — redirect to payment page
            // Store orderId and clientSecret for payment step
            setStep('payment');
            setPendingPayment({ orderId: order.id, clientSecret: intent.clientSecret, amount: intent.amount });
        } catch (e: any) { setError(e.message); }
        finally { setBooking(false); }
    };

    const cancelOrder = async (orderId: string) => {
        if (!window.confirm('Cancel this dumpster order? This cannot be undone.')) return;
        try {
            await api.cancelDumpsterOrder(orderId);
            await loadOrders();
        } catch (e: any) { setError(e.message); }
    };

    const saveEdit = async () => {
        if (!editingOrder) return;
        try {
            await api.updateDumpsterOrder(editingOrder.id, editForm);
            setEditingOrder(null);
            await loadOrders();
        } catch (e: any) { setError(e.message); }
    };

    const requestPickup = async (orderId: string) => {
        try {
            await api.requestDumpsterPickup(orderId, '');
            await loadOrders();
        } catch (e: any) { setError(e.message); }
    };

    const statusColor = (s: string) => {
        switch (s) {
            case 'Completed': return C.green;
            case 'Delivered': return C.greenMid;
            case 'VendorAccepted': case 'DeliveryScheduled': return '#3730A3';
            case 'PickupRequested': case 'PickupScheduled': return '#854F0B';
            case 'Cancelled': return C.coral;
            default: return C.textMuted;
        }
    };

    const statusLabel = (s: string) => ({
        Pending: '⏳ Awaiting vendor',
        VendorAccepted: '✓ Confirmed',
        DeliveryScheduled: '🚛 Delivery scheduled',
        Delivered: '📦 On site',
        LoadingInProgress: '💪 Loading in progress',
        PickupRequested: '🔔 Pickup requested',
        PickupScheduled: '🚛 Pickup scheduled',
        Completed: '✅ Completed',
        Cancelled: '✗ Cancelled',
    }[s] || s);

    return (
        <div>
            <Alert message={error} />


            {/* Browse vendors */}
            {step === 'browse' && (
                <div>
                    <div style={{ marginBottom: 20 }}>
                        <h3 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700 }}>Dumpster Rental</h3>
                        <p style={{ margin: 0, color: C.textSub, fontSize: 14 }}>Choose a size and we'll connect you with a local vendor. Add optional loading runners to help fill it.</p>
                    </div>
                    {loading ? <Spinner /> : vendors.length === 0 ? (
                        <Card style={{ textAlign: 'center', padding: 48 }}>
                            <div style={{ fontSize: 40 }}>🚛</div>
                            <div style={{ fontSize: 16, fontWeight: 600, marginTop: 12 }}>No vendors in your area yet</div>
                            <div style={{ color: C.textSub, marginTop: 6 }}>We're expanding! Check back soon.</div>
                        </Card>
                    ) : vendors.map(vendor => (
                        <Card key={vendor.id} style={{ marginBottom: 20 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>🚛 {vendor.companyName}</div>
                                    <div style={{ fontSize: 13, color: C.textSub }}>{vendor.serviceAreaRadiusMiles} mile service area · {vendor.contactName}</div>
                                </div>
                                <span style={{ background: C.greenBg, color: C.green, borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 600 }}>Available</span>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                                {vendor.sizes.filter((s: any) => s.isAvailable).map((size: any) => (
                                    <div key={size.id} style={{ border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, cursor: 'pointer', transition: 'all 0.15s' }}
                                        onClick={() => selectSize(vendor, size)}
                                        onMouseEnter={e => (e.currentTarget.style.borderColor = C.greenMid)}
                                        onMouseLeave={e => (e.currentTarget.style.borderColor = C.border)}>
                                        <div style={{ fontSize: 36, fontWeight: 900, color: C.green, marginBottom: 4 }}>{size.yards}yd</div>
                                        <div style={{ fontSize: 13, color: C.textSub, marginBottom: 12, minHeight: 36 }}>{size.description}</div>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 2 }}>
                                            ${size.deliveryPrice.toFixed(2)} delivery
                                        </div>
                                        <div style={{ fontSize: 12, color: C.textMuted }}>
                                            + ${size.dailyRate.toFixed(2)}/day rental
                                        </div>
                                        <div style={{ marginTop: 12 }}>
                                            <Btn size="sm" fullWidth onClick={() => selectSize(vendor, size)}>Select →</Btn>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            {/* Configure booking */}
            {(step === 'configure' || step === 'confirm') && selectedSize && (
                <div>
                    {/* Breadcrumb */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, fontSize: 13, color: C.textSub }}>
                        <button onClick={() => setStep('browse')} style={{ background: 'none', border: 'none', color: C.greenMid, cursor: 'pointer', padding: 0, fontWeight: 600 }}>← Browse</button>
                        <span>›</span>
                        <span style={{ color: C.text }}>{selectedVendor?.companyName} — {selectedSize.yards}yd dumpster</span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 24, alignItems: 'start' }}>
                        {/* Form */}
                        <Card>
                            <h3 style={{ margin: '0 0 20px', fontSize: 16, fontWeight: 700 }}>📋 Booking details</h3>
                            <AddressAutocomplete
                                label="Delivery address *"
                                value={address}
                                onChange={({ address: a, lat, lng, city, state, postalCode, country }) => {
                                    setAddress(a);
                                    setDeliveryLat(lat);
                                    setDeliveryLng(lng);
                                    setDeliveryCity(city); setDeliveryState(state); setDeliveryPostalCode(postalCode); setDeliveryCountry(country);
                                }}
                                placeholder="Enter delivery address"
                                required
                            />
                            <Input label="Requested delivery date *" value={deliveryDate} onChange={setDeliveryDate} type="date" />
                            <div style={{ marginBottom: 16 }}>
                                <label style={{ fontSize: 13, fontWeight: 500, color: C.textSub, display: 'block', marginBottom: 6 }}>Rental duration</label>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    {[3, 5, 7, 10, 14].map(d => (
                                        <button key={d} onClick={() => { setRentalDays(d); getQuote(selectedSize.id); }}
                                            style={{ padding: '8px 16px', borderRadius: 8, border: `2px solid ${rentalDays === d ? C.greenMid : C.border}`, background: rentalDays === d ? C.greenBg : '#fff', color: rentalDays === d ? C.green : C.textSub, fontWeight: rentalDays === d ? 700 : 400, fontSize: 13, cursor: 'pointer' }}>
                                            {d} days
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Loading service */}
                            {loadingJobsEnabled && (
                            <div style={{ border: `1px solid ${loadingRequested ? C.greenMid : C.border}`, borderRadius: 12, padding: 16, marginBottom: 16, background: loadingRequested ? C.greenBg : '#fff' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: loadingRequested ? 12 : 0 }}>
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: 14 }}>💪 Add loading runners</div>
                                        <div style={{ fontSize: 12, color: C.textSub, marginTop: 2 }}>${quote?.loadingHourlyRate ?? 10}/hr per runner — we'll help fill the dumpster</div>
                                    </div>
                                    <button onClick={() => setLoadingRequested(!loadingRequested)}
                                        style={{ width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', background: loadingRequested ? C.greenMid : C.border, position: 'relative', transition: 'all 0.2s' }}>
                                        <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: loadingRequested ? 22 : 2, transition: 'left 0.2s' }} />
                                    </button>
                                </div>
                                {loadingRequested && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <span style={{ fontSize: 13, color: C.textSub }}>Number of runners:</span>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <button onClick={() => setRunnersRequested(Math.max(1, runnersRequested - 1))}
                                                style={{ width: 28, height: 28, borderRadius: '50%', border: `1px solid ${C.border}`, background: '#fff', cursor: 'pointer', fontWeight: 700 }}>−</button>
                                            <span style={{ fontWeight: 700, fontSize: 16, minWidth: 20, textAlign: 'center' }}>{runnersRequested}</span>
                                            <button onClick={() => setRunnersRequested(Math.min(4, runnersRequested + 1))}
                                                style={{ width: 28, height: 28, borderRadius: '50%', border: `1px solid ${C.border}`, background: '#fff', cursor: 'pointer', fontWeight: 700 }}>+</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            )}

                            <Btn fullWidth size="lg" onClick={confirmBooking} disabled={booking || !address || !deliveryLat || !deliveryLng || !deliveryDate}>
                                {booking ? 'Booking...' : 'Confirm booking →'}
                            </Btn>
                        </Card>

                        {/* Quote summary */}
                        <div>
                            <Card style={{ position: 'sticky', top: 20 }}>
                                <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700 }}>💰 Price summary</h3>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14 }}>
                                    <span style={{ color: C.textSub }}>{selectedSize.yards}yd dumpster delivery</span>
                                    <span style={{ fontWeight: 600 }}>${selectedSize.deliveryPrice.toFixed(2)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14 }}>
                                    <span style={{ color: C.textSub }}>Rental ({rentalDays} days × ${selectedSize.dailyRate}/day)</span>
                                    <span style={{ fontWeight: 600 }}>${(selectedSize.dailyRate * rentalDays).toFixed(2)}</span>
                                </div>
                                {loadingRequested && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14 }}>
                                        <span style={{ color: C.textSub }}>Loading runners ({runnersRequested}× ${quote?.loadingHourlyRate ?? 10}/hr)</span>
                                        <span style={{ fontWeight: 600, color: C.textMuted }}>Billed by hour</span>
                                    </div>
                                )}
                                <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 12, paddingTop: 12, display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontWeight: 700 }}>Estimated total</span>
                                    <span style={{ fontWeight: 800, fontSize: 18, color: C.greenMid }}>
                                        ${(selectedSize.deliveryPrice + selectedSize.dailyRate * rentalDays).toFixed(2)}
                                        {loadingRequested ? '+' : ''}
                                    </span>
                                </div>
                                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 6 }}>Plus applicable sales tax, calculated at checkout.</div>
                                {loadingRequested && (
                                    <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4, textAlign: 'right' }}>
                                        + loading fees billed after completion
                                    </div>
                                )}
                                <div style={{ marginTop: 16, padding: 12, background: C.bg, borderRadius: 8 }}>
                                    <div style={{ fontSize: 12, color: C.textSub, lineHeight: 1.6 }}>
                                        🚛 <strong>{selectedVendor?.companyName}</strong><br />
                                        📦 {selectedSize.yards}yd · up to {selectedSize.maxRentalDays} days max<br />
                                        📍 {selectedSize.description}
                                    </div>
                                </div>
                            </Card>
                        </div>
                    </div>
                </div>
            )}

            {/* My Orders */}
            {step === 'payment' && pendingPayment && (
                <div style={{ maxWidth: 480, margin: '0 auto' }}>
                    <Alert message={error} />
                    <div style={{ background: C.greenBg, borderRadius: 10, padding: 14, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 600 }}>🚛 Dumpster rental</span>
                        <span style={{ fontWeight: 800, fontSize: 20, color: C.greenMid }}>${pendingPayment.amount.toFixed(2)}</span>
                    </div>
                    <PaymentForm
                        jobId={pendingPayment.orderId}
                        amount={pendingPayment.amount}
                        clientSecret={pendingPayment.clientSecret}
                        onSuccess={async () => {
                            await confirmAuthWithRetry(() => api.confirmDumpsterAuthorization(pendingPayment.orderId), "dumpster");
                            setPendingPayment(null);
                            onComplete?.();
                        }}
                        onCancel={() => {
                            api.cancelDumpsterOrder(pendingPayment.orderId).catch(() => {});
                            setPendingPayment(null);
                            setStep('configure');
                        }}
                    />
                </div>
            )}

        </div>
    );
}

// ── Dumpster Runner Map Modal ─────────────────────────────────────────────────

function formatEtaWeb(minutes: number): string {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h === 0) return `${m}m`;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function DumpsterRunnerMapModal({
    order,
    job,
    location,
    onClose,
}: {
    order: any;
    job: any;
    location: any;
    onClose: () => void;
}) {
    const branding = useBranding();
    const mapDivRef = useRef<HTMLDivElement>(null);
    const googleMapRef = useRef<any>(null);
    const runnerMarkerRef = useRef<any>(null);
    const deliveryMarkerRef = useRef<any>(null);
    const directionsRendererRef = useRef<any>(null);
    const [ready, setReady] = useState(false);

    // Wait for Google Maps SDK (loaded globally via script tag in index.html)
    useEffect(() => {
        const check = () => {
            if ((window as any).google?.maps) setReady(true);
            else setTimeout(check, 200);
        };
        check();
    }, []);

    // Init map once SDK is ready
    useEffect(() => {
        if (!ready || !mapDivRef.current || googleMapRef.current) return;
        const g = (window as any).google.maps;
        const center = order.deliveryLat && order.deliveryLng
            ? { lat: order.deliveryLat, lng: order.deliveryLng }
            : { lat: 34.7304, lng: -86.5861 };

        googleMapRef.current = new g.Map(mapDivRef.current, {
            center,
            zoom: 13,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
        });

        // Delivery address marker (green pin)
        if (order.deliveryLat && order.deliveryLng) {
            deliveryMarkerRef.current = new g.Marker({
                position: center,
                map: googleMapRef.current,
                title: order.deliveryAddress,
                icon: {
                    path: g.SymbolPath.CIRCLE,
                    scale: 10,
                    fillColor: branding.primaryColor,
                    fillOpacity: 1,
                    strokeColor: '#fff',
                    strokeWeight: 2,
                },
            });
        }
    }, [ready]);

    // Update runner marker and route when location changes
    useEffect(() => {
        if (!ready || !googleMapRef.current || !location) return;
        if (!location.lat || !location.lng || location.lat === 0) return;

        const g = (window as any).google.maps;
        const runnerPos = { lat: location.lat, lng: location.lng };

        if (runnerMarkerRef.current) {
            runnerMarkerRef.current.setPosition(runnerPos);
        } else {
            runnerMarkerRef.current = new g.Marker({
                position: runnerPos,
                map: googleMapRef.current,
                title: job?.contractorName || 'Runner',
                icon: {
                    path: g.SymbolPath.FORWARD_CLOSED_ARROW,
                    scale: 8,
                    fillColor: '#3730A3',
                    fillOpacity: 1,
                    strokeColor: '#fff',
                    strokeWeight: 2,
                },
            });
        }

        // Fetch and draw route
        if (order.deliveryLat && order.deliveryLng) {
            if (directionsRendererRef.current) {
                directionsRendererRef.current.setMap(null);
            }
            const ds = new g.DirectionsService();
            const dr = new g.DirectionsRenderer({
                map: googleMapRef.current,
                suppressMarkers: true,
                polylineOptions: { strokeColor: '#3730A3', strokeWeight: 4, strokeOpacity: 0.8 },
            });
            directionsRendererRef.current = dr;
            ds.route({
                origin: runnerPos,
                destination: { lat: order.deliveryLat, lng: order.deliveryLng },
                travelMode: g.TravelMode.DRIVING,
            }, (result: any, status: any) => {
                if (status === 'OK') dr.setDirections(result);
            });

            // Fit map to show both markers
            const bounds = new g.LatLngBounds();
            bounds.extend(runnerPos);
            bounds.extend({ lat: order.deliveryLat, lng: order.deliveryLng });
            googleMapRef.current.fitBounds(bounds, { top: 80, right: 40, bottom: 40, left: 40 });
        }
    }, [ready, location?.lat, location?.lng]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            directionsRendererRef.current?.setMap(null);
            runnerMarkerRef.current?.setMap(null);
            deliveryMarkerRef.current?.setMap(null);
        };
    }, []);

    const eta = location?.etaMinutes ?? null;
    const distance = location?.distanceMiles ?? null;
    const statusLabel = job?.status === 'EnRoute' ? '🚗 En route' : '📍 On site';

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', width: '100%', maxWidth: 600, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
                {/* Header */}
                <div style={{ background: '#3730A3', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: '6px 12px' }}>
                        ← Back
                    </button>
                    <span style={{ color: '#fff', fontSize: 16, fontWeight: 700, flex: 1 }}>Track Runner</span>
                    <span style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 600 }}>
                        {statusLabel}
                    </span>
                </div>

                {/* ETA bar */}
                {(eta != null || distance != null) && job?.status === 'EnRoute' && (
                    <div style={{ background: '#EEF2FF', padding: '12px 20px', display: 'flex', gap: 24, alignItems: 'center', borderBottom: '1px solid #C7D2FE' }}>
                        {eta != null && (
                            <div>
                                <span style={{ fontSize: 22, fontWeight: 800, color: '#3730A3' }}>{formatEtaWeb(eta)}</span>
                                <span style={{ fontSize: 12, color: '#6366F1', marginLeft: 6 }}>away</span>
                            </div>
                        )}
                        {distance != null && (
                            <div>
                                <span style={{ fontSize: 22, fontWeight: 800, color: '#374151' }}>{distance.toFixed(1)}</span>
                                <span style={{ fontSize: 12, color: '#6B7280', marginLeft: 4 }}>miles</span>
                            </div>
                        )}
                        {job?.contractorName && (
                            <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                                <div style={{ fontSize: 13, fontWeight: 700 }}>{job.contractorName}</div>
                                <div style={{ fontSize: 12, color: '#6B7280' }}>Loading runner</div>
                            </div>
                        )}
                    </div>
                )}
                {job?.status === 'OnSite' && (
                    <div style={{ background: '#EEF2FF', padding: '12px 20px', borderBottom: '1px solid #C7D2FE', textAlign: 'center' }}>
                        <span style={{ fontSize: 15, fontWeight: 700, color: '#3730A3' }}>📍 Runner has arrived!</span>
                        {job?.contractorName && <span style={{ fontSize: 13, color: '#6366F1', marginLeft: 8 }}>{job.contractorName} is on site</span>}
                    </div>
                )}
                {!location && (
                    <div style={{ background: '#F9FAFB', padding: '10px 20px', borderBottom: `1px solid ${C.border}`, textAlign: 'center' }}>
                        <span style={{ fontSize: 13, color: C.textMuted }}>Waiting for runner location...</span>
                    </div>
                )}

                {/* Map */}
                <div style={{ flex: 1, minHeight: 360, position: 'relative' }}>
                    {!ready && (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textSub }}>
                            Loading map...
                        </div>
                    )}
                    <div ref={mapDivRef} style={{ width: '100%', height: '100%', minHeight: 360, display: ready ? 'block' : 'none' }} />
                </div>

                {/* Footer */}
                <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.border}`, background: C.bg }}>
                    <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 2 }}>Delivery address</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{order.deliveryAddress}</div>
                </div>
            </div>
        </div>
    );
}
